import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  Container,
  Paper,
  Typography,
  CircularProgress,
  Alert,
  Tabs,
  Tab,
  TextField,
  IconButton,
  Tooltip,
  Button,
  Link as MuiLink,
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import SaveIcon from '@mui/icons-material/Save';
import CancelIcon from '@mui/icons-material/Cancel';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import TableChartIcon from '@mui/icons-material/TableChart';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import DownloadIcon from '@mui/icons-material/Download';
import { useAuth } from '../context/AuthContext';
import Header from '../components/Header';
import AttachmentViewer from '../components/AttachmentViewer';
import { API_ENDPOINTS, API_BASE_URL, getFileURL } from '../config/apiClient';
import { generatePDFFromElement } from '../utils/pdfGenerator';

// Import xlsx
const XLSX = require('xlsx');

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`budget-tabpanel-${index}`}
      aria-labelledby={`budget-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ pt: 3 }}>{children}</Box>}
    </div>
  );
}

interface CommitmentData {
  id: string;
  documentId: string;
  name: string;
  commitment: number;
  paid: number;
  due: number;
  firstInstallment: number;
  secondInstallment: number;
  thirdInstallment: number;
  fourthInstallment: number;
  fifthInstallment: number;
  sixthInstallment: number;
  seventhInstallment: number;
  eighthInstallment: number;
  ninthInstallment: number;
  tenthInstallment: number;
  commitmentUpdatedBy: string;
  remarks: string;
  createdAt?: string;
  updatedAt?: string;
  publishedAt?: string;
}

interface SavasinData {
  id: string;
  documentId: string;
  name: string;
  familyName: string;
  contactNumber: string;
  commitment: number;
  paid: number;
  due: number;
  remarks: string;
  createdAt?: string;
  updatedAt?: string;
  publishedAt?: string;
}

interface AttachmentFile {
  id: number;
  documentId?: string;
  name: string;
  url: string;
  mime: string;
  ext: string;
  size: number;
}

interface OtherSourceData {
  id: string;
  documentId: string;
  Item: string;
  Date: string;
  Amount: number;
  Comment: string;
  attachment: AttachmentFile | null;
  createdAt?: string;
  updatedAt?: string;
  publishedAt?: string;
}

interface MemberData {
  id: string;
  documentId: string;
  name: string;
  familyName: string;
  contactNumber: string;
  commitment: number;
  paid: number;
  due: number;
  remarks: string;
  createdAt?: string;
  updatedAt?: string;
  publishedAt?: string;
}

const BudgetCommitment: React.FC = () => {
  const { user } = useAuth();
  const [tabValue, setTabValue] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  // Commitment data
  const [commitmentData, setCommitmentData] = useState<CommitmentData[]>([]);
  const [editingCommitmentId, setEditingCommitmentId] = useState<string | null>(null);
  const [editingCommitmentData, setEditingCommitmentData] = useState<CommitmentData | null>(null);
  const [savingCommitment, setSavingCommitment] = useState(false);
  const [addingCommitment, setAddingCommitment] = useState(false);
  const [newCommitmentData, setNewCommitmentData] = useState<Partial<CommitmentData> | null>(null);

  // Savasin data
  const [savasinData, setSavasinData] = useState<SavasinData[]>([]);
  const [editingSavasinId, setEditingSavasinId] = useState<string | null>(null);
  const [editingSavasinData, setEditingSavasinData] = useState<SavasinData | null>(null);
  const [savingSavasin, setSavingSavasin] = useState(false);
  const [addingSavasin, setAddingSavasin] = useState(false);
  const [newSavasinData, setNewSavasinData] = useState<Partial<SavasinData> | null>(null);

  // Other Sources data
  const [otherSourcesData, setOtherSourcesData] = useState<OtherSourceData[]>([]);
  const [editingOtherSourceId, setEditingOtherSourceId] = useState<string | null>(null);
  const [editingOtherSourceData, setEditingOtherSourceData] = useState<OtherSourceData | null>(null);
  const [savingOtherSource, setSavingOtherSource] = useState(false);
  const [addingOtherSource, setAddingOtherSource] = useState(false);
  const [newOtherSourceData, setNewOtherSourceData] = useState<Partial<OtherSourceData> | null>(null);
  const [newOtherSourceFile, setNewOtherSourceFile] = useState<File | null>(null);
  const [editOtherSourceFile, setEditOtherSourceFile] = useState<File | null>(null);
  const [removeEditAttachment, setRemoveEditAttachment] = useState(false);
  const newOtherSourceFileRef = useRef<HTMLInputElement>(null);
  const editOtherSourceFileRef = useRef<HTMLInputElement>(null);
  const [otherSourceViewerOpen, setOtherSourceViewerOpen] = useState(false);
  const [selectedOtherSourceAttachment, setSelectedOtherSourceAttachment] = useState<AttachmentFile | null>(null);

  // Members data
  const [membersData, setMembersData] = useState<MemberData[]>([]);
  const [editingMemberId, setEditingMemberId] = useState<string | null>(null);
  const [editingMemberData, setEditingMemberData] = useState<MemberData | null>(null);
  const [savingMember, setSavingMember] = useState(false);
  const [addingMember, setAddingMember] = useState(false);
  const [newMemberData, setNewMemberData] = useState<Partial<MemberData> | null>(null);

  // PDF download state
  const [downloadingPDF, setDownloadingPDF] = useState(false);

  // Spreadsheet download state
  const [downloadingSpreadsheet, setDownloadingSpreadsheet] = useState(false);

  const isMaintainer = user && user.role && (
    user.role.name === 'Maintainer' || 
    user.role.name === 'Maintainers' || 
    user.role.name?.toLowerCase().includes('maintain')
  );

  const fetchCommitmentData = async () => {
    try {
      const token = localStorage.getItem('strapiToken');
      // Fetch all records using offset pagination to bypass Strapi's max limit
      const url = new URL(`${API_BASE_URL}${API_ENDPOINTS.COMMITMENTS.LIST}`);
      url.searchParams.append('pagination[pageSize]', '5000');
      url.searchParams.append('pagination[page]', '1');
      
      console.log('Fetching from:', url.toString());
      
      const response = await fetch(url.toString(), {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          ...(token && { Authorization: `Bearer ${token}` }),
        },
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch commitments: ${response.statusText}`);
      }

      const responseData = await response.json();
      const commitments = Array.isArray(responseData?.data) ? responseData.data : [];
      
      const formattedData: CommitmentData[] = commitments.map((item: any) => ({
        id: item.id,
        documentId: item.documentId || item.id,
        name: item.name || '',
        commitment: item.commitment || 0,
        paid: item.paid || 0,
        due: item.due || 0,
        firstInstallment: item.firstInstallment || 0,
        secondInstallment: item.secondInstallment || 0,
        thirdInstallment: item.thirdInstallment || 0,
        fourthInstallment: item.fourthInstallment || 0,
        fifthInstallment: item.fifthInstallment || 0,
        sixthInstallment: item.sixthInstallment || 0,
        seventhInstallment: item.seventhInstallment || 0,
        eighthInstallment: item.eighthInstallment || 0,
        ninthInstallment: item.ninthInstallment || 0,
        tenthInstallment: item.tenthInstallment || 0,
        commitmentUpdatedBy: item.commitmentUpdatedBy || '',
        remarks: item.remarks || '',
        createdAt: item.createdAt,
        updatedAt: item.updatedAt,
        publishedAt: item.publishedAt,
      }));

      console.log(`✓ Loaded ${formattedData.length} commitment records from API (Total: ${responseData?.pagination?.total || formattedData.length})`);
      setCommitmentData(formattedData);
    } catch (err) {
      console.error('Error fetching commitments:', err);
      setError(err instanceof Error ? err.message : 'Failed to fetch commitment data');
    }
  };

  const fetchSavasinData = async () => {
    try {
      const token = localStorage.getItem('strapiToken');
      const url = new URL(`${API_BASE_URL}${API_ENDPOINTS.SAVASIN_DETAILS.LIST}`);
      url.searchParams.append('pagination[pageSize]', '5000');
      url.searchParams.append('pagination[page]', '1');

      const response = await fetch(url.toString(), {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          ...(token && { Authorization: `Bearer ${token}` }),
        },
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch savasin details: ${response.statusText}`);
      }

      const responseData = await response.json();
      const savasins = Array.isArray(responseData?.data) ? responseData.data : [];

      const formattedData: SavasinData[] = savasins.map((item: any) => ({
        id: item.id,
        documentId: item.documentId || item.id,
        name: item.name || '',
        familyName: item.familyName || '',
        contactNumber: item.contactNumber || '',
        commitment: item.commitment || 0,
        paid: item.paid || 0,
        due: item.due || 0,
        remarks: item.remarks || '',
        createdAt: item.createdAt,
        updatedAt: item.updatedAt,
        publishedAt: item.publishedAt,
      }));

      console.log(`✓ Loaded ${formattedData.length} savasin records from API`);
      setSavasinData(formattedData);
    } catch (err) {
      console.error('Error fetching savasin details:', err);
    }
  };

  const fetchMembersData = async () => {
    try {
      const token = localStorage.getItem('strapiToken');
      const url = new URL(`${API_BASE_URL}${API_ENDPOINTS.ALL_MEMBERS.LIST}`);
      url.searchParams.append('pagination[pageSize]', '5000');
      url.searchParams.append('pagination[page]', '1');

      const response = await fetch(url.toString(), {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          ...(token && { Authorization: `Bearer ${token}` }),
        },
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch members: ${response.statusText}`);
      }

      const responseData = await response.json();
      const members = Array.isArray(responseData?.data) ? responseData.data : [];

      const formattedData: MemberData[] = members.map((item: any) => ({
        id: item.id,
        documentId: item.documentId || item.id,
        name: item.name || '',
        familyName: item.familyName || '',
        contactNumber: item.contactNumber || '',
        commitment: item.commitment || 0,
        paid: item.paid || 0,
        due: item.due || 0,
        remarks: item.remarks || '',
        createdAt: item.createdAt,
        updatedAt: item.updatedAt,
        publishedAt: item.publishedAt,
      }));

      console.log(`✓ Loaded ${formattedData.length} member records from API`);
      setMembersData(formattedData);
    } catch (err) {
      console.error('Error fetching members:', err);
    }
  };

  const fetchOtherSourcesData = async () => {
    try {
      const token = localStorage.getItem('strapiToken');
      const url = new URL(`${API_BASE_URL}${API_ENDPOINTS.OTHER_SOURCES.LIST}`);
      url.searchParams.append('pagination[pageSize]', '5000');
      url.searchParams.append('pagination[page]', '1');
      url.searchParams.append('populate', 'attachment');

      const response = await fetch(url.toString(), {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          ...(token && { Authorization: `Bearer ${token}` }),
        },
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch other sources: ${response.statusText}`);
      }

      const responseData = await response.json();
      const sources = Array.isArray(responseData?.data) ? responseData.data : [];

      const formattedData: OtherSourceData[] = sources.map((item: any) => {
        let attachment: AttachmentFile | null = null;
        const rawAtt = Array.isArray(item.attachment) ? item.attachment[0] : item.attachment;
        if (rawAtt) {
          attachment = {
            id: rawAtt.id,
            documentId: rawAtt.documentId,
            name: rawAtt.name || '',
            url: rawAtt.url || '',
            mime: rawAtt.mime || '',
            ext: rawAtt.ext || '',
            size: rawAtt.size || 0,
          };
        }
        return {
          id: item.id,
          documentId: item.documentId || item.id,
          Item: item.Item || '',
          Date: item.Date || '',
          Amount: item.Amount || 0,
          Comment: item.Comment || '',
          attachment,
          createdAt: item.createdAt,
          updatedAt: item.updatedAt,
          publishedAt: item.publishedAt,
        };
      });

      console.log(`✓ Loaded ${formattedData.length} other source records from API`);
      setOtherSourcesData(formattedData);
    } catch (err) {
      console.error('Error fetching other sources:', err);
    }
  };

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        setError(null);
        await fetchCommitmentData();
        await fetchSavasinData();
        await fetchOtherSourcesData();
        await fetchMembersData();
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  const handleTabChange = (_event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  const handleEditCommitment = (row: CommitmentData) => {
    setEditingCommitmentId(row.documentId);
    setEditingCommitmentData({ ...row });
  };

  const handleSaveCommitment = async () => {
    if (!editingCommitmentData) return;

    try {
      setSavingCommitment(true);
      const token = localStorage.getItem('strapiToken');
      const documentId = editingCommitmentData.documentId || editingCommitmentData.id;
      const url = `${API_BASE_URL}${API_ENDPOINTS.COMMITMENTS.UPDATE(documentId)}`;

      console.log('DEBUG: Saving commitment...');
      console.log('  DocumentID:', documentId);
      console.log('  URL:', url);

      const response = await fetch(url, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          ...(token && { Authorization: `Bearer ${token}` }),
        },
        body: JSON.stringify({
          data: {
            name: editingCommitmentData.name,
            commitment: editingCommitmentData.commitment,
            paid: editingCommitmentData.paid,
            due: editingCommitmentData.due,
            firstInstallment: editingCommitmentData.firstInstallment,
            secondInstallment: editingCommitmentData.secondInstallment,
            thirdInstallment: editingCommitmentData.thirdInstallment,
            fourthInstallment: editingCommitmentData.fourthInstallment,
            fifthInstallment: editingCommitmentData.fifthInstallment,
            sixthInstallment: editingCommitmentData.sixthInstallment,
            seventhInstallment: editingCommitmentData.seventhInstallment,
            eighthInstallment: editingCommitmentData.eighthInstallment,
            ninthInstallment: editingCommitmentData.ninthInstallment,
            tenthInstallment: editingCommitmentData.tenthInstallment,
            commitmentUpdatedBy: editingCommitmentData.commitmentUpdatedBy,
            remarks: editingCommitmentData.remarks,
          },
        }),
      });

      console.log('DEBUG: Response status:', response.status, response.statusText);

      if (!response.ok) {
        const errorText = await response.text();
        console.log('DEBUG: Error response:', errorText);
        throw new Error(`Failed to update commitment: ${response.statusText}`);
      }

      // Update local data
      setCommitmentData(
        commitmentData.map((item) =>
          item.documentId === editingCommitmentData.documentId ? editingCommitmentData : item
        )
      );

      setSuccessMessage(`Commitment for ${editingCommitmentData.name} updated successfully`);
      setTimeout(() => setSuccessMessage(null), 3000);

      setEditingCommitmentId(null);
      setEditingCommitmentData(null);
    } catch (err) {
      const errorMsg = err instanceof Error ? err.message : 'Failed to save commitment';
      setError(errorMsg);
      console.error('Error saving commitment:', err);
    } finally {
      setSavingCommitment(false);
    }
  };

  const handleCancelCommitment = () => {
    setEditingCommitmentId(null);
    setEditingCommitmentData(null);
  };

  const handleChangeEditingCommitmentData = (field: keyof CommitmentData, value: any) => {
    if (editingCommitmentData) {
      setEditingCommitmentData({
        ...editingCommitmentData,
        [field]: value,
      });
    }
  };

  const handleEditSavasin = (row: SavasinData) => {
    setEditingSavasinId(row.documentId);
    setEditingSavasinData({ ...row });
  };

  const handleSaveSavasin = async () => {
    if (!editingSavasinData) return;
    try {
      setSavingSavasin(true);
      const token = localStorage.getItem('strapiToken');
      const documentId = editingSavasinData.documentId || editingSavasinData.id;
      const url = `${API_BASE_URL}${API_ENDPOINTS.SAVASIN_DETAILS.UPDATE(documentId)}`;

      const response = await fetch(url, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          ...(token && { Authorization: `Bearer ${token}` }),
        },
        body: JSON.stringify({
          data: {
            name: editingSavasinData.name,
            familyName: editingSavasinData.familyName,
            contactNumber: editingSavasinData.contactNumber,
            commitment: editingSavasinData.commitment,
            paid: editingSavasinData.paid,
            due: editingSavasinData.due,
            remarks: editingSavasinData.remarks,
          },
        }),
      });

      if (!response.ok) {
        throw new Error(`Failed to update savasin detail: ${response.statusText}`);
      }

      setSavasinData(
        savasinData.map((item) =>
          item.documentId === editingSavasinData.documentId ? editingSavasinData : item
        )
      );

      setSuccessMessage(`Savasin detail updated successfully`);
      setTimeout(() => setSuccessMessage(null), 3000);

      setEditingSavasinId(null);
      setEditingSavasinData(null);
    } catch (err) {
      const errorMsg = err instanceof Error ? err.message : 'Failed to save savasin detail';
      setError(errorMsg);
      console.error('Error saving savasin detail:', err);
    } finally {
      setSavingSavasin(false);
    }
  };

  const handleCancelSavasin = () => {
    setEditingSavasinId(null);
    setEditingSavasinData(null);
  };

  const handleChangeSavasinData = (field: keyof SavasinData, value: any) => {
    if (editingSavasinData) {
      setEditingSavasinData({
        ...editingSavasinData,
        [field]: value,
      });
    }
  };

  const handleEditMember = (row: MemberData) => {
    setEditingMemberId(row.documentId);
    setEditingMemberData({ ...row });
  };

  const handleSaveMember = async () => {
    if (!editingMemberData) return;
    try {
      setSavingMember(true);
      const token = localStorage.getItem('strapiToken');
      const documentId = editingMemberData.documentId || editingMemberData.id;
      const url = `${API_BASE_URL}${API_ENDPOINTS.ALL_MEMBERS.UPDATE(documentId)}`;

      const response = await fetch(url, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          ...(token && { Authorization: `Bearer ${token}` }),
        },
        body: JSON.stringify({
          data: {
            name: editingMemberData.name,
            familyName: editingMemberData.familyName,
            contactNumber: editingMemberData.contactNumber,
            commitment: editingMemberData.commitment,
            paid: editingMemberData.paid,
            due: editingMemberData.due,
            remarks: editingMemberData.remarks,
          },
        }),
      });

      if (!response.ok) {
        throw new Error(`Failed to update member: ${response.statusText}`);
      }

      setMembersData(
        membersData.map((item) =>
          item.documentId === editingMemberData.documentId ? editingMemberData : item
        )
      );

      setSuccessMessage(`Member updated successfully`);
      setTimeout(() => setSuccessMessage(null), 3000);

      setEditingMemberId(null);
      setEditingMemberData(null);
    } catch (err) {
      const errorMsg = err instanceof Error ? err.message : 'Failed to save member';
      setError(errorMsg);
      console.error('Error saving member:', err);
    } finally {
      setSavingMember(false);
    }
  };

  const handleCancelMember = () => {
    setEditingMemberId(null);
    setEditingMemberData(null);
  };

  const handleChangeMemberData = (field: keyof MemberData, value: any) => {
    if (editingMemberData) {
      setEditingMemberData({
        ...editingMemberData,
        [field]: value,
      });
    }
  };

  // Other Sources Edit/Save/Cancel Handlers
  const handleEditOtherSource = (row: OtherSourceData) => {
    setEditingOtherSourceId(row.documentId);
    setEditingOtherSourceData({ ...row });
  };

  const handleSaveOtherSource = async () => {
    if (!editingOtherSourceData) return;
    try {
      setSavingOtherSource(true);
      const token = localStorage.getItem('strapiToken');
      const documentId = editingOtherSourceData.documentId || editingOtherSourceData.id;
      const url = `${API_BASE_URL}${API_ENDPOINTS.OTHER_SOURCES.UPDATE(documentId)}`;

      // Step 1: Upload new file if provided
      let attachmentId: number | null | undefined = undefined; // undefined = no change
      if (editOtherSourceFile) {
        const formData = new FormData();
        formData.append('files', editOtherSourceFile);
        const uploadResponse = await fetch(`${API_BASE_URL}${API_ENDPOINTS.UPLOAD.FILE}`, {
          method: 'POST',
          headers: {
            ...(token && { Authorization: `Bearer ${token}` }),
          },
          body: formData,
        });
        if (uploadResponse.ok) {
          const uploadData = await uploadResponse.json();
          attachmentId = Array.isArray(uploadData) ? uploadData[0]?.id : uploadData?.id;
        }
      } else if (removeEditAttachment) {
        attachmentId = null; // explicitly remove
      }

      const updateData: any = {
        Item: editingOtherSourceData.Item,
        Date: editingOtherSourceData.Date,
        Amount: editingOtherSourceData.Amount,
        Comment: editingOtherSourceData.Comment,
      };
      if (attachmentId !== undefined) {
        updateData.attachment = attachmentId;
      }

      const response = await fetch(url, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          ...(token && { Authorization: `Bearer ${token}` }),
        },
        body: JSON.stringify({ data: updateData }),
      });

      if (!response.ok) {
        throw new Error(`Failed to update other source: ${response.statusText}`);
      }

      setSuccessMessage(`Other source record updated successfully`);
      setTimeout(() => setSuccessMessage(null), 3000);

      setEditingOtherSourceId(null);
      setEditingOtherSourceData(null);
      setEditOtherSourceFile(null);
      setRemoveEditAttachment(false);
      await fetchOtherSourcesData();
    } catch (err) {
      const errorMsg = err instanceof Error ? err.message : 'Failed to save other source';
      setError(errorMsg);
      console.error('Error saving other source:', err);
    } finally {
      setSavingOtherSource(false);
    }
  };

  const handleCancelOtherSource = () => {
    setEditingOtherSourceId(null);
    setEditingOtherSourceData(null);
    setEditOtherSourceFile(null);
    setRemoveEditAttachment(false);
  };

  const handleChangeOtherSourceData = (field: keyof OtherSourceData, value: any) => {
    if (editingOtherSourceData) {
      setEditingOtherSourceData({
        ...editingOtherSourceData,
        [field]: value,
      });
    }
  };

  // Add Row Handlers
  const handleAddCommitmentRow = () => {
    setAddingCommitment(true);
    setNewCommitmentData({
      name: '',
      commitment: 0,
      paid: 0,
      due: 0,
      firstInstallment: 0,
      secondInstallment: 0,
      thirdInstallment: 0,
      fourthInstallment: 0,
      fifthInstallment: 0,
      sixthInstallment: 0,
      seventhInstallment: 0,
      eighthInstallment: 0,
      ninthInstallment: 0,
      tenthInstallment: 0,
      commitmentUpdatedBy: '',
      remarks: '',
    });
  };

  const handleSaveNewCommitment = async () => {
    if (!newCommitmentData) return;
    try {
      setSavingCommitment(true);
      const token = localStorage.getItem('strapiToken');
      const url = `${API_BASE_URL}${API_ENDPOINTS.COMMITMENTS.LIST}`;

      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token && { Authorization: `Bearer ${token}` }),
        },
        body: JSON.stringify({
          data: {
            name: newCommitmentData.name,
            commitment: newCommitmentData.commitment || 0,
            paid: newCommitmentData.paid || 0,
            due: newCommitmentData.due || 0,
            firstInstallment: newCommitmentData.firstInstallment || 0,
            secondInstallment: newCommitmentData.secondInstallment || 0,
            thirdInstallment: newCommitmentData.thirdInstallment || 0,
            fourthInstallment: newCommitmentData.fourthInstallment || 0,
            fifthInstallment: newCommitmentData.fifthInstallment || 0,
            sixthInstallment: newCommitmentData.sixthInstallment || 0,
            seventhInstallment: newCommitmentData.seventhInstallment || 0,
            eighthInstallment: newCommitmentData.eighthInstallment || 0,
            ninthInstallment: newCommitmentData.ninthInstallment || 0,
            tenthInstallment: newCommitmentData.tenthInstallment || 0,
            commitmentUpdatedBy: newCommitmentData.commitmentUpdatedBy || '',
            remarks: newCommitmentData.remarks || '',
          },
        }),
      });

      if (!response.ok) {
        throw new Error(`Failed to create commitment: ${response.statusText}`);
      }

      setSuccessMessage('Commitment record created successfully');
      setTimeout(() => setSuccessMessage(null), 3000);
      setAddingCommitment(false);
      setNewCommitmentData(null);
      await fetchCommitmentData();
    } catch (err) {
      const errorMsg = err instanceof Error ? err.message : 'Failed to create commitment';
      setError(errorMsg);
      console.error('Error creating commitment:', err);
    } finally {
      setSavingCommitment(false);
    }
  };

  const handleCancelAddCommitment = () => {
    setAddingCommitment(false);
    setNewCommitmentData(null);
  };

  const handleDeleteCommitment = async (documentId: string) => {
    if (!window.confirm('Are you sure you want to delete this commitment record?')) return;
    try {
      setSavingCommitment(true);
      const token = localStorage.getItem('strapiToken');
      const url = `${API_BASE_URL}${API_ENDPOINTS.COMMITMENTS.UPDATE(documentId)}`;

      const response = await fetch(url, {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
          ...(token && { Authorization: `Bearer ${token}` }),
        },
      });

      if (!response.ok) {
        throw new Error(`Failed to delete commitment: ${response.statusText}`);
      }

      setCommitmentData(commitmentData.filter((item) => item.documentId !== documentId));
      setSuccessMessage('Commitment record deleted successfully');
      setTimeout(() => setSuccessMessage(null), 3000);
    } catch (err) {
      const errorMsg = err instanceof Error ? err.message : 'Failed to delete commitment';
      setError(errorMsg);
      console.error('Error deleting commitment:', err);
    } finally {
      setSavingCommitment(false);
    }
  };

  // Savasin Add/Delete Handlers
  const handleAddSavasinRow = () => {
    setAddingSavasin(true);
    setNewSavasinData({
      name: '',
      familyName: '',
      contactNumber: '',
      commitment: 0,
      paid: 0,
      due: 0,
      remarks: '',
    });
  };

  const handleSaveNewSavasin = async () => {
    if (!newSavasinData) return;
    try {
      setSavingSavasin(true);
      const token = localStorage.getItem('strapiToken');
      const url = `${API_BASE_URL}${API_ENDPOINTS.SAVASIN_DETAILS.LIST}`;

      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token && { Authorization: `Bearer ${token}` }),
        },
        body: JSON.stringify({
          data: {
            name: newSavasinData.name,
            familyName: newSavasinData.familyName || '',
            contactNumber: newSavasinData.contactNumber || '',
            commitment: newSavasinData.commitment || 0,
            paid: newSavasinData.paid || 0,
            due: newSavasinData.due || 0,
            remarks: newSavasinData.remarks || '',
          },
        }),
      });

      if (!response.ok) {
        throw new Error(`Failed to create savasin record: ${response.statusText}`);
      }

      setSuccessMessage('Savasin record created successfully');
      setTimeout(() => setSuccessMessage(null), 3000);
      setAddingSavasin(false);
      setNewSavasinData(null);
      await fetchSavasinData();
    } catch (err) {
      const errorMsg = err instanceof Error ? err.message : 'Failed to create savasin record';
      setError(errorMsg);
      console.error('Error creating savasin record:', err);
    } finally {
      setSavingSavasin(false);
    }
  };

  const handleCancelAddSavasin = () => {
    setAddingSavasin(false);
    setNewSavasinData(null);
  };

  const handleDeleteSavasin = async (documentId: string) => {
    if (!window.confirm('Are you sure you want to delete this savasin record?')) return;
    try {
      setSavingSavasin(true);
      const token = localStorage.getItem('strapiToken');
      const url = `${API_BASE_URL}${API_ENDPOINTS.SAVASIN_DETAILS.UPDATE(documentId)}`;

      const response = await fetch(url, {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
          ...(token && { Authorization: `Bearer ${token}` }),
        },
      });

      if (!response.ok) {
        throw new Error(`Failed to delete savasin record: ${response.statusText}`);
      }

      setSavasinData(savasinData.filter((item) => item.documentId !== documentId));
      setSuccessMessage('Savasin record deleted successfully');
      setTimeout(() => setSuccessMessage(null), 3000);
    } catch (err) {
      const errorMsg = err instanceof Error ? err.message : 'Failed to delete savasin record';
      setError(errorMsg);
      console.error('Error deleting savasin record:', err);
    } finally {
      setSavingSavasin(false);
    }
  };

  // Members Add/Delete Handlers
  const handleAddMemberRow = () => {
    setAddingMember(true);
    setNewMemberData({
      name: '',
      familyName: '',
      contactNumber: '',
      commitment: 0,
      paid: 0,
      due: 0,
      remarks: '',
    });
  };

  const handleSaveNewMember = async () => {
    if (!newMemberData) return;
    try {
      setSavingMember(true);
      const token = localStorage.getItem('strapiToken');
      const url = `${API_BASE_URL}${API_ENDPOINTS.ALL_MEMBERS.LIST}`;

      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token && { Authorization: `Bearer ${token}` }),
        },
        body: JSON.stringify({
          data: {
            name: newMemberData.name,
            familyName: newMemberData.familyName || '',
            contactNumber: newMemberData.contactNumber || '',
            commitment: newMemberData.commitment || 0,
            paid: newMemberData.paid || 0,
            due: newMemberData.due || 0,
            remarks: newMemberData.remarks || '',
          },
        }),
      });

      if (!response.ok) {
        throw new Error(`Failed to create member record: ${response.statusText}`);
      }

      setSuccessMessage('Member record created successfully');
      setTimeout(() => setSuccessMessage(null), 3000);
      setAddingMember(false);
      setNewMemberData(null);
      await fetchMembersData();
    } catch (err) {
      const errorMsg = err instanceof Error ? err.message : 'Failed to create member record';
      setError(errorMsg);
      console.error('Error creating member record:', err);
    } finally {
      setSavingMember(false);
    }
  };

  const handleCancelAddMember = () => {
    setAddingMember(false);
    setNewMemberData(null);
  };

  const handleDeleteMember = async (documentId: string) => {
    if (!window.confirm('Are you sure you want to delete this member record?')) return;
    try {
      setSavingMember(true);
      const token = localStorage.getItem('strapiToken');
      const url = `${API_BASE_URL}${API_ENDPOINTS.ALL_MEMBERS.UPDATE(documentId)}`;

      const response = await fetch(url, {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
          ...(token && { Authorization: `Bearer ${token}` }),
        },
      });

      if (!response.ok) {
        throw new Error(`Failed to delete member record: ${response.statusText}`);
      }

      setMembersData(membersData.filter((item) => item.documentId !== documentId));
      setSuccessMessage('Member record deleted successfully');
      setTimeout(() => setSuccessMessage(null), 3000);
    } catch (err) {
      const errorMsg = err instanceof Error ? err.message : 'Failed to delete member record';
      setError(errorMsg);
      console.error('Error deleting member record:', err);
    } finally {
      setSavingMember(false);
    }
  };

  // Other Sources Add/Delete Handlers
  const handleAddOtherSourceRow = () => {
    setAddingOtherSource(true);
    setNewOtherSourceData({
      Item: '',
      Date: '',
      Amount: 0,
      Comment: '',
    });
  };

  const handleSaveNewOtherSource = async () => {
    if (!newOtherSourceData) return;
    try {
      setSavingOtherSource(true);
      const token = localStorage.getItem('strapiToken');
      const url = `${API_BASE_URL}${API_ENDPOINTS.OTHER_SOURCES.LIST}`;

      // Step 1: Create the record
      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token && { Authorization: `Bearer ${token}` }),
        },
        body: JSON.stringify({
          data: {
            Item: newOtherSourceData.Item || '',
            Date: newOtherSourceData.Date || null,
            Amount: newOtherSourceData.Amount || 0,
            Comment: newOtherSourceData.Comment || '',
          },
        }),
      });

      if (!response.ok) {
        throw new Error(`Failed to create other source record: ${response.statusText}`);
      }

      const createdData = await response.json();
      const createdDocId = createdData?.data?.documentId || createdData?.data?.id;

      // Step 2: Upload file and attach if provided
      if (newOtherSourceFile && createdDocId) {
        const formData = new FormData();
        formData.append('files', newOtherSourceFile);
        const uploadResponse = await fetch(`${API_BASE_URL}${API_ENDPOINTS.UPLOAD.FILE}`, {
          method: 'POST',
          headers: {
            ...(token && { Authorization: `Bearer ${token}` }),
          },
          body: formData,
        });
        if (uploadResponse.ok) {
          const uploadData = await uploadResponse.json();
          const fileId = Array.isArray(uploadData) ? uploadData[0]?.id : uploadData?.id;
          if (fileId) {
            await fetch(`${API_BASE_URL}${API_ENDPOINTS.OTHER_SOURCES.UPDATE(createdDocId)}`, {
              method: 'PUT',
              headers: {
                'Content-Type': 'application/json',
                ...(token && { Authorization: `Bearer ${token}` }),
              },
              body: JSON.stringify({ data: { attachment: fileId } }),
            });
          }
        }
      }

      setSuccessMessage('Other source record created successfully');
      setTimeout(() => setSuccessMessage(null), 3000);
      setAddingOtherSource(false);
      setNewOtherSourceData(null);
      setNewOtherSourceFile(null);
      await fetchOtherSourcesData();
    } catch (err) {
      const errorMsg = err instanceof Error ? err.message : 'Failed to create other source record';
      setError(errorMsg);
      console.error('Error creating other source record:', err);
    } finally {
      setSavingOtherSource(false);
    }
  };

  const handleCancelAddOtherSource = () => {
    setAddingOtherSource(false);
    setNewOtherSourceData(null);
    setNewOtherSourceFile(null);
  };

  const handleDeleteOtherSource = async (documentId: string) => {
    if (!window.confirm('Are you sure you want to delete this other source record?')) return;
    try {
      setSavingOtherSource(true);
      const token = localStorage.getItem('strapiToken');
      const url = `${API_BASE_URL}${API_ENDPOINTS.OTHER_SOURCES.UPDATE(documentId)}`;

      const response = await fetch(url, {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
          ...(token && { Authorization: `Bearer ${token}` }),
        },
      });

      if (!response.ok) {
        throw new Error(`Failed to delete other source record: ${response.statusText}`);
      }

      setOtherSourcesData(otherSourcesData.filter((item) => item.documentId !== documentId));
      setSuccessMessage('Other source record deleted successfully');
      setTimeout(() => setSuccessMessage(null), 3000);
    } catch (err) {
      const errorMsg = err instanceof Error ? err.message : 'Failed to delete other source record';
      setError(errorMsg);
      console.error('Error deleting other source record:', err);
    } finally {
      setSavingOtherSource(false);
    }
  };

  const handleDownloadReportPDF = async () => {
    try {
      setDownloadingPDF(true);
      const timestamp = new Date().toLocaleString('en-IN', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
      }).replace(/[/:]/g, '-');
      
      const filename = `Budget_Commitment_Report_${timestamp}.pdf`;
      
      await generatePDFFromElement('budget-report-pdf-content', filename, {
        orientation: 'portrait',
        format: 'a4',
        margin: 10,
      });
    } catch (err) {
      console.error('Error downloading PDF:', err);
      alert('Failed to download PDF. Please try again.');
    } finally {
      setDownloadingPDF(false);
    }
  };

  const handleDownloadSpreadsheet = async () => {
    try {
      setDownloadingSpreadsheet(true);
      
      // Create a new workbook
      const wb = XLSX.utils.book_new();
      
      // Prepare Commitment data
      const commitmentSheetData = commitmentData.map((item) => ({
        'ID': item.id,
        'Name': item.name,
        'Commitment': item.commitment,
        'Paid': item.paid,
        'Due': item.due,
        'First Installment': item.firstInstallment,
        'Second Installment': item.secondInstallment,
        'Third Installment': item.thirdInstallment,
        'Fourth Installment': item.fourthInstallment,
        'Fifth Installment': item.fifthInstallment,
        'Sixth Installment': item.sixthInstallment,
        'Seventh Installment': item.seventhInstallment,
        'Eighth Installment': item.eighthInstallment,
        'Ninth Installment': item.ninthInstallment,
        'Tenth Installment': item.tenthInstallment,
        'Updated By': item.commitmentUpdatedBy,
        'Remarks': item.remarks,
      }));
      const commitmentWs = XLSX.utils.json_to_sheet(commitmentSheetData);
      XLSX.utils.book_append_sheet(wb, commitmentWs, 'Commitment');
      
      // Prepare Savasin data
      const savasinSheetData = savasinData.map((item) => ({
        'ID': item.id,
        'Name': item.name,
        'Family Name': item.familyName,
        'Contact Number': item.contactNumber,
        'Commitment': item.commitment,
        'Paid': item.paid,
        'Due': item.due,
        'Remarks': item.remarks,
      }));
      const savasinWs = XLSX.utils.json_to_sheet(savasinSheetData);
      XLSX.utils.book_append_sheet(wb, savasinWs, 'Savasin');
      
      // Prepare Members data
      const membersSheetData = membersData.map((item) => ({
        'ID': item.id,
        'Name': item.name,
        'Family Name': item.familyName,
        'Contact Number': item.contactNumber,
        'Commitment': item.commitment,
        'Paid': item.paid,
        'Due': item.due,
        'Remarks': item.remarks,
      }));
      const membersWs = XLSX.utils.json_to_sheet(membersSheetData);
      XLSX.utils.book_append_sheet(wb, membersWs, 'All Members');
      
      // Prepare Other Sources data
      const otherSourcesSheetData = otherSourcesData.map((item, index) => ({
        'Sl No': index + 1,
        'Item': item.Item,
        'Date': item.Date,
        'Amount': item.Amount,
        'Comment': item.Comment,
      }));
      const otherSourcesWs = XLSX.utils.json_to_sheet(otherSourcesSheetData);
      XLSX.utils.book_append_sheet(wb, otherSourcesWs, 'Other Sources');
      
      // Generate timestamp
      const timestamp = new Date().toLocaleString('en-IN', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
      }).replace(/[/:]/g, '-');
      
      // Write the file
      XLSX.writeFile(wb, `Budget_Commitment_Data_${timestamp}.xlsx`);
    } catch (err) {
      console.error('Error downloading spreadsheet:', err);
      alert('Failed to download spreadsheet. Please try again.');
    } finally {
      setDownloadingSpreadsheet(false);
    }
  };

  return (
    <Box sx={{ minHeight: '100vh', backgroundColor: '#f5f5f5' }}>
      <Header />
      <Container maxWidth="lg" sx={{ py: { xs: 2, md: 4 }, px: { xs: 1, sm: 2 } }}>
        <Paper elevation={3} sx={{ p: { xs: 2, md: 3 } }}>
          <Typography variant="h4" gutterBottom sx={{ mb: 3, fontSize: { xs: '1.5rem', sm: '2rem', md: '2.125rem' } }}>
            Budget & Commitment Dashboard
          </Typography>

          {isMaintainer && (
            <Alert severity="info" sx={{ mb: 2 }}>
              ✓ You are logged in as: <strong>{user?.username}</strong> | Role: <strong>{user?.role?.name}</strong>
            </Alert>
          )}

          {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
          {successMessage && <Alert severity="success" sx={{ mb: 2 }}>{successMessage}</Alert>}

          <Box sx={{ position: 'relative', mb: 3, overflowX: 'auto' }}>
            <Tabs 
              value={tabValue} 
              onChange={handleTabChange} 
              sx={{ 
                borderBottom: 1, 
                borderColor: 'divider',
                minWidth: { xs: '100%', md: 'auto' },
                '& .MuiTab-root': {
                  fontSize: { xs: '0.75rem', sm: '0.875rem', md: '1rem' },
                  padding: { xs: '8px 12px', md: '12px 16px' },
                  minWidth: { xs: 'auto', md: '160px' }
                }
              }}
              variant="scrollable"
              scrollButtons="auto"
            >
              <Tab label="Report" id="budget-tab-0" aria-controls="budget-tabpanel-0" />
              <Tab label={`Commitment`} title={`Commitment (${commitmentData.length} records)`} id="budget-tab-1" aria-controls="budget-tabpanel-1" />
              <Tab label="Savasin" id="budget-tab-2" aria-controls="budget-tabpanel-2" />
              <Tab label="Other Sources" id="budget-tab-3" aria-controls="budget-tabpanel-3" />
              <Tab label="Members" id="budget-tab-4" aria-controls="budget-tabpanel-4" />
            </Tabs>
            <Box sx={{ position: { xs: 'relative', md: 'absolute' }, top: { xs: 'auto', md: 8 }, right: { xs: 'auto', md: 0 }, zIndex: 10, display: 'flex', alignItems: 'center', gap: 1, mt: { xs: 1, md: 0 } }}>
              <Typography variant="caption" sx={{ fontWeight: 500, color: '#666', display: { xs: 'none', md: 'block' } }}>
                Download as Combined Spreadsheet
              </Typography>
              <Tooltip title="Download all data (Commitment, Savasin, Other Sources, All Members) in separate sheets">
                <IconButton
                  color="primary"
                  onClick={handleDownloadSpreadsheet}
                  disabled={downloadingSpreadsheet}
                  size="small"
                  sx={{ 
                    backgroundColor: '#f5f5f5',
                    '&:hover': { backgroundColor: '#eeeeee' },
                    width: { xs: 44, md: 40 },
                    height: { xs: 44, md: 40 }
                  }}
                >
                  <TableChartIcon />
                </IconButton>
              </Tooltip>
            </Box>
          </Box>

          <TabPanel value={tabValue} index={0}>
            {loading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 400 }}>
                <CircularProgress />
              </Box>
            ) : (
              <Box sx={{ position: 'relative' }}>
                <Box id="budget-report-pdf-content">
                  <Box sx={{ position: 'absolute', top: 0, right: 0, zIndex: 10, display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Typography variant="caption" sx={{ fontWeight: 500, color: '#666', display: { xs: 'none', md: 'block' } }}>
                      Download Report as PDF
                    </Typography>
                    <Tooltip title="Download report with all statistics and collection summaries">
                      <IconButton
                        color="primary"
                        onClick={handleDownloadReportPDF}
                        disabled={downloadingPDF}
                        size="small"
                        sx={{ 
                          backgroundColor: '#f5f5f5',
                          '&:hover': { backgroundColor: '#eeeeee' },
                          width: { xs: 44, md: 40 },
                          height: { xs: 44, md: 40 }
                        }}
                      >
                        <FileDownloadIcon />
                      </IconButton>
                    </Tooltip>
                  </Box>
                  <Typography variant="h5" sx={{ mb: 3, fontWeight: 'bold', textAlign: 'center', fontSize: { xs: '1.25rem', md: '1.5rem' } }}>
                    Commitment & Other Sources Report Dashboard
                  </Typography>
                  <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr', md: '1fr 1fr 1fr' }, gap: 2, mb: 4 }}>
                    {/* Total Budget */}
                    <Paper sx={{ p: 3, backgroundColor: '#e3f2fd', borderLeft: '4px solid #2196f3' }}>
                      <Typography color="textSecondary" gutterBottom>
                        Total Budget Amount
                      </Typography>
                      <Typography variant="h4" sx={{ fontWeight: 'bold', color: '#1976d2' }}>
                        ₹{(commitmentData.reduce((sum, item) => sum + (item.commitment || 0), 0) + 
                            savasinData.reduce((sum, item) => sum + (item.commitment || 0), 0) +
                            otherSourcesData.reduce((sum, item) => sum + (item.Amount || 0), 0)).toLocaleString('en-IN')}
                      </Typography>
                      <Box sx={{ mt: 1, display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                        <Typography variant="body2" sx={{ color: 'textSecondary', fontWeight: 600, fontSize: '0.95rem' }}>
                          Total Commitment Amount: ₹{(commitmentData.reduce((sum, item) => sum + (item.commitment || 0), 0) + 
                            savasinData.reduce((sum, item) => sum + (item.commitment || 0), 0)).toLocaleString('en-IN')}
                        </Typography>
                        <Typography variant="caption" sx={{ color: 'textSecondary' }}>
                          Income from Other Sources: ₹{otherSourcesData.reduce((sum, item) => sum + (item.Amount || 0), 0).toLocaleString('en-IN')}
                        </Typography>
                      </Box>
                    </Paper>

                    {/* Total Received */}
                    <Paper sx={{ p: 3, backgroundColor: '#e8f5e9', borderLeft: '4px solid #4caf50' }}>
                      <Typography color="textSecondary" gutterBottom>
                        Total Amount Received
                      </Typography>
                      <Typography variant="h4" sx={{ fontWeight: 'bold', color: '#388e3c' }}>
                        ₹{(commitmentData.reduce((sum, item) => sum + (item.paid || 0), 0) + 
                            savasinData.reduce((sum, item) => sum + (item.paid || 0), 0) +
                            otherSourcesData.reduce((sum, item) => sum + (item.Amount || 0), 0)).toLocaleString('en-IN')}
                      </Typography>
                      <Box sx={{ mt: 1, display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                        <Typography variant="body2" sx={{ color: 'textSecondary', fontWeight: 600, fontSize: '0.95rem' }}>
                          Commitment Paid: ₹{(commitmentData.reduce((sum, item) => sum + (item.paid || 0), 0) + 
                            savasinData.reduce((sum, item) => sum + (item.paid || 0), 0)).toLocaleString('en-IN')}
                        </Typography>
                        <Typography variant="caption" sx={{ color: 'textSecondary' }}>
                          Other Sources: ₹{otherSourcesData.reduce((sum, item) => sum + (item.Amount || 0), 0).toLocaleString('en-IN')}
                        </Typography>
                      </Box>
                    </Paper>

                    {/* Total Due */}
                    <Paper sx={{ p: 3, backgroundColor: '#fff3e0', borderLeft: '4px solid #ff9800' }}>
                      <Typography color="textSecondary" gutterBottom>
                        Total Amount Due
                      </Typography>
                      <Typography variant="h4" sx={{ fontWeight: 'bold', color: '#f57c00' }}>
                        ₹{(commitmentData.reduce((sum, item) => sum + (item.due || 0), 0) + 
                            savasinData.reduce((sum, item) => sum + (item.due || 0), 0)).toLocaleString('en-IN')}
                      </Typography>
                      <Typography variant="caption" sx={{ color: 'textSecondary' }}>
                        Pending collection
                      </Typography>
                    </Paper>

                    {/* Members Who Paid */}
                    <Paper sx={{ p: 3, backgroundColor: '#f3e5f5', borderLeft: '4px solid #9c27b0' }}>
                      <Typography color="textSecondary" gutterBottom>
                        Members with Partial/Full Payment
                      </Typography>
                      <Typography variant="h4" sx={{ fontWeight: 'bold', color: '#7b1fa2' }}>
                        {commitmentData.filter(item => item.paid > 0).length + 
                         savasinData.filter(item => item.paid > 0).length}
                      </Typography>
                      <Typography variant="caption" sx={{ color: 'textSecondary' }}>
                        Out of {commitmentData.length + savasinData.length} total
                      </Typography>
                    </Paper>

                    {/* Pending from Members Who Paid */}
                    <Paper sx={{ p: 3, backgroundColor: '#fce4ec', borderLeft: '4px solid #e91e63' }}>
                      <Typography color="textSecondary" gutterBottom>
                        Pending from Active Members
                      </Typography>
                      <Typography variant="h4" sx={{ fontWeight: 'bold', color: '#c2185b' }}>
                        ₹{(commitmentData.filter(item => item.paid > 0).reduce((sum, item) => sum + (item.due || 0), 0) + 
                            savasinData.filter(item => item.paid > 0).reduce((sum, item) => sum + (item.due || 0), 0)).toLocaleString('en-IN')}
                      </Typography>
                      <Typography variant="caption" sx={{ color: 'textSecondary' }}>
                        Due from members who have paid
                      </Typography>
                    </Paper>
                  </Box>

                  {/* Collection Summary by Category */}
                  <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr 1fr' }, gap: 2 }}>
                    {/* Commitment Summary */}
                    <Paper sx={{ p: 3, backgroundColor: '#f5f5f5' }}>
                      <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mb: 2 }}>
                        Commitment Collection
                      </Typography>
                      <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1 }}>
                        <Typography variant="body2">Members Count:</Typography>
                        <Typography variant="body2" sx={{ fontWeight: 'bold' }}>{commitmentData.length}</Typography>
                        <Typography variant="body2">Total Committed:</Typography>
                        <Typography variant="body2" sx={{ fontWeight: 'bold', color: '#1976d2' }}>
                          ₹{commitmentData.reduce((sum, item) => sum + (item.commitment || 0), 0).toLocaleString('en-IN')}
                        </Typography>
                        <Typography variant="body2">Total Paid:</Typography>
                        <Typography variant="body2" sx={{ fontWeight: 'bold', color: '#388e3c' }}>
                          ₹{commitmentData.reduce((sum, item) => sum + (item.paid || 0), 0).toLocaleString('en-IN')}
                        </Typography>
                        <Typography variant="body2">Total Due:</Typography>
                        <Typography variant="body2" sx={{ fontWeight: 'bold', color: '#f57c00' }}>
                          ₹{commitmentData.reduce((sum, item) => sum + (item.due || 0), 0).toLocaleString('en-IN')}
                        </Typography>
                        <Typography variant="body2">Payment Rate:</Typography>
                        <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                          {commitmentData.length > 0 && commitmentData.reduce((sum, item) => sum + (item.commitment || 0), 0) > 0
                            ? ((commitmentData.reduce((sum, item) => sum + (item.paid || 0), 0) / commitmentData.reduce((sum, item) => sum + (item.commitment || 0), 0)) * 100).toFixed(1) + '%'
                            : 'N/A'}
                        </Typography>
                      </Box>
                    </Paper>

                    {/* Savasin Summary */}
                    <Paper sx={{ p: 3, backgroundColor: '#f5f5f5' }}>
                      <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mb: 2 }}>
                        Savasin Details
                      </Typography>
                      <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1 }}>
                        <Typography variant="body2">Members Count:</Typography>
                        <Typography variant="body2" sx={{ fontWeight: 'bold' }}>{savasinData.length}</Typography>
                        <Typography variant="body2">Total Committed:</Typography>
                        <Typography variant="body2" sx={{ fontWeight: 'bold', color: '#1976d2' }}>
                          ₹{savasinData.reduce((sum, item) => sum + (item.commitment || 0), 0).toLocaleString('en-IN')}
                        </Typography>
                        <Typography variant="body2">Total Paid:</Typography>
                        <Typography variant="body2" sx={{ fontWeight: 'bold', color: '#388e3c' }}>
                          ₹{savasinData.reduce((sum, item) => sum + (item.paid || 0), 0).toLocaleString('en-IN')}
                        </Typography>
                        <Typography variant="body2">Total Due:</Typography>
                        <Typography variant="body2" sx={{ fontWeight: 'bold', color: '#f57c00' }}>
                          ₹{savasinData.reduce((sum, item) => sum + (item.due || 0), 0).toLocaleString('en-IN')}
                        </Typography>
                        <Typography variant="body2">Payment Rate:</Typography>
                        <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                          {savasinData.length > 0 && savasinData.reduce((sum, item) => sum + (item.commitment || 0), 0) > 0
                            ? ((savasinData.reduce((sum, item) => sum + (item.paid || 0), 0) / savasinData.reduce((sum, item) => sum + (item.commitment || 0), 0)) * 100).toFixed(1) + '%'
                            : 'N/A'}
                        </Typography>
                      </Box>
                    </Paper>

                    {/* Income from Other Sources Summary */}
                    <Paper sx={{ p: 3, backgroundColor: '#f5f5f5' }}>
                      <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mb: 2 }}>
                        Income from Other Sources
                      </Typography>
                      <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1, mb: 1 }}>
                        <Typography variant="body2">Total Entries:</Typography>
                        <Typography variant="body2" sx={{ fontWeight: 'bold' }}>{otherSourcesData.length}</Typography>
                        <Typography variant="body2">Total Income:</Typography>
                        <Typography variant="body2" sx={{ fontWeight: 'bold' }}>₹{otherSourcesData.reduce((sum, item) => sum + (item.Amount || 0), 0).toLocaleString('en-IN')}</Typography>
                      </Box>
                      {otherSourcesData.length > 0 && (
                        <Box sx={{ mt: 1, borderTop: '1px solid #e0e0e0', pt: 1 }}>
                          {otherSourcesData.map((item, index) => (
                            <Box key={item.id} sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 0.5, py: 0.5, borderBottom: index < otherSourcesData.length - 1 ? '1px dashed #e0e0e0' : 'none' }}>
                              <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                                {index + 1}. {item.Item}
                              </Typography>
                              <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                                ₹{(item.Amount || 0).toLocaleString('en-IN')}
                              </Typography>
                            </Box>
                          ))}
                        </Box>
                      )}
                    </Paper>
                  </Box>
                </Box>
              </Box>
            )}
          </TabPanel>

          <TabPanel value={tabValue} index={1}>
            {loading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 400 }}>
                <CircularProgress />
              </Box>
            ) : (
              <Box sx={{ border: '1px solid #e0e0e0', borderRadius: 1, overflow: 'hidden', maxHeight: 700, display: 'flex', flexDirection: 'column' }}>
                {/* Single scrollable container for both header and body */}
                <Box
                  sx={{
                    overflowY: 'auto',
                    overflowX: 'auto',
                    flex: 1,
                    display: 'flex',
                    flexDirection: 'column',
                  }}
                >
                  {/* Table Header with Add Button */}
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', p: { xs: 0.75, md: 1 }, backgroundColor: '#fff', borderBottom: '1px solid #e0e0e0', gap: 1 }}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 'bold', fontSize: { xs: '0.813rem', md: '1rem' }, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      Commitment ({commitmentData.length})
                    </Typography>
                    {isMaintainer && (
                      <Tooltip title="Add new commitment record">
                        <IconButton
                          size="small"
                          color="primary"
                          onClick={handleAddCommitmentRow}
                          disabled={addingCommitment}
                        >
                          <AddIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    )}
                  </Box>

                  {/* Table Column Headers - Sticky to top, scrolls horizontally with body */}
                  <Box
                    sx={{
                      display: 'flex',
                      backgroundColor: '#f5f5f5',
                      borderBottom: '2px solid #e0e0e0',
                      fontWeight: 'bold',
                      fontSize: '0.875rem',
                      position: 'sticky',
                      top: 0,
                      zIndex: 5,
                      minWidth: 'min-content',
                      flexShrink: 0,
                    }}
                  >
                    <Box sx={{ flex: '0 0 60px', minWidth: 60, p: 1 }}>ID</Box>
                    <Box sx={{ flex: '0 0 280px', minWidth: 280, p: 1 }}>Name</Box>
                    <Box sx={{ flex: '0 0 120px', minWidth: 120, p: 1, textAlign: 'right' }}>Commitment</Box>
                    <Box sx={{ flex: '0 0 120px', minWidth: 120, p: 1, textAlign: 'right' }}>Paid</Box>
                    <Box sx={{ flex: '0 0 120px', minWidth: 120, p: 1, textAlign: 'right' }}>Due</Box>
                    <Box sx={{ flex: '0 0 100px', minWidth: 100, p: 1, textAlign: 'right' }}>1st Inst.</Box>
                    <Box sx={{ flex: '0 0 100px', minWidth: 100, p: 1, textAlign: 'right' }}>2nd Inst.</Box>
                    <Box sx={{ flex: '0 0 100px', minWidth: 100, p: 1, textAlign: 'right' }}>3rd Inst.</Box>
                    <Box sx={{ flex: '0 0 100px', minWidth: 100, p: 1, textAlign: 'right' }}>4th Inst.</Box>
                    <Box sx={{ flex: '0 0 100px', minWidth: 100, p: 1, textAlign: 'right' }}>5th Inst.</Box>
                    <Box sx={{ flex: '0 0 100px', minWidth: 100, p: 1, textAlign: 'right' }}>6th Inst.</Box>
                    <Box sx={{ flex: '0 0 100px', minWidth: 100, p: 1, textAlign: 'right' }}>7th Inst.</Box>
                    <Box sx={{ flex: '0 0 100px', minWidth: 100, p: 1, textAlign: 'right' }}>8th Inst.</Box>
                    <Box sx={{ flex: '0 0 100px', minWidth: 100, p: 1, textAlign: 'right' }}>9th Inst.</Box>
                    <Box sx={{ flex: '0 0 100px', minWidth: 100, p: 1, textAlign: 'right' }}>10th Inst.</Box>
                    <Box sx={{ flex: '0 0 150px', minWidth: 150, p: 1 }}>Updated By</Box>
                    <Box sx={{ flex: '0 0 200px', minWidth: 200, p: 1 }}>Remarks</Box>
                    <Box sx={{ flex: '0 0 100px', minWidth: 100, p: 1 }}>Actions</Box>
                  </Box>

                  {/* New Commitment Row Form */}
                  {addingCommitment && (
                    <Box
                      sx={{
                        display: 'flex',
                        borderBottom: '2px solid #1976d2',
                        backgroundColor: '#e3f2fd',
                        fontSize: '0.875rem',
                        minWidth: 'min-content',
                      }}
                    >
                      <Box sx={{ flex: '0 0 60px', minWidth: 60, p: 1 }}>-</Box>
                      <Box sx={{ flex: '0 0 280px', minWidth: 280, p: 1 }}>
                        <TextField
                          size="small"
                          placeholder="Name"
                          value={newCommitmentData?.name || ''}
                          onChange={(e) => setNewCommitmentData({ ...newCommitmentData, name: e.target.value })}
                          fullWidth
                        />
                      </Box>
                      <Box sx={{ flex: '0 0 120px', minWidth: 120, p: 1 }}>
                        <TextField
                          size="small"
                          type="number"
                          placeholder="Amount"
                          value={newCommitmentData?.commitment || 0}
                          onChange={(e) => setNewCommitmentData({ ...newCommitmentData, commitment: Number(e.target.value) })}
                          fullWidth
                        />
                      </Box>
                      <Box sx={{ flex: '0 0 120px', minWidth: 120, p: 1 }}>
                        <TextField
                          size="small"
                          type="number"
                          placeholder="Paid"
                          value={newCommitmentData?.paid || 0}
                          onChange={(e) => setNewCommitmentData({ ...newCommitmentData, paid: Number(e.target.value) })}
                          fullWidth
                        />
                      </Box>
                      <Box sx={{ flex: '0 0 120px', minWidth: 120, p: 1 }}>
                        <TextField
                          size="small"
                          type="number"
                          placeholder="Due"
                          value={newCommitmentData?.due || 0}
                          onChange={(e) => setNewCommitmentData({ ...newCommitmentData, due: Number(e.target.value) })}
                          fullWidth
                        />
                      </Box>
                      <Box sx={{ flex: '0 0 120px', minWidth: 120, p: 1 }}>
                        <TextField
                          size="small"
                          type="number"
                          placeholder="1st"
                          value={newCommitmentData?.firstInstallment || 0}
                          onChange={(e) => setNewCommitmentData({ ...newCommitmentData, firstInstallment: Number(e.target.value) })}
                          fullWidth
                        />
                      </Box>
                      <Box sx={{ flex: '0 0 120px', minWidth: 120, p: 1 }}>
                        <TextField
                          size="small"
                          type="number"
                          placeholder="2nd"
                          value={newCommitmentData?.secondInstallment || 0}
                          onChange={(e) => setNewCommitmentData({ ...newCommitmentData, secondInstallment: Number(e.target.value) })}
                          fullWidth
                        />
                      </Box>
                      <Box sx={{ flex: '0 0 110px', minWidth: 110, p: 1 }}>
                        <TextField
                          size="small"
                          type="number"
                          placeholder="3rd"
                          value={newCommitmentData?.thirdInstallment || 0}
                          onChange={(e) => setNewCommitmentData({ ...newCommitmentData, thirdInstallment: Number(e.target.value) })}
                          fullWidth
                        />
                      </Box>
                      <Box sx={{ flex: '0 0 110px', minWidth: 110, p: 1 }}>
                        <TextField
                          size="small"
                          type="number"
                          placeholder="4th"
                          value={newCommitmentData?.fourthInstallment || 0}
                          onChange={(e) => setNewCommitmentData({ ...newCommitmentData, fourthInstallment: Number(e.target.value) })}
                          fullWidth
                        />
                      </Box>
                      <Box sx={{ flex: '0 0 110px', minWidth: 110, p: 1 }}>
                        <TextField
                          size="small"
                          type="number"
                          placeholder="5th"
                          value={newCommitmentData?.fifthInstallment || 0}
                          onChange={(e) => setNewCommitmentData({ ...newCommitmentData, fifthInstallment: Number(e.target.value) })}
                          fullWidth
                        />
                      </Box>
                      <Box sx={{ flex: '0 0 100px', minWidth: 100, p: 1 }}>
                        <TextField
                          size="small"
                          type="number"
                          placeholder="6th"
                          value={newCommitmentData?.sixthInstallment || 0}
                          onChange={(e) => setNewCommitmentData({ ...newCommitmentData, sixthInstallment: Number(e.target.value) })}
                          fullWidth
                        />
                      </Box>
                      <Box sx={{ flex: '0 0 100px', minWidth: 100, p: 1 }}>
                        <TextField
                          size="small"
                          type="number"
                          placeholder="7th"
                          value={newCommitmentData?.seventhInstallment || 0}
                          onChange={(e) => setNewCommitmentData({ ...newCommitmentData, seventhInstallment: Number(e.target.value) })}
                          fullWidth
                        />
                      </Box>
                      <Box sx={{ flex: '0 0 100px', minWidth: 100, p: 1 }}>
                        <TextField
                          size="small"
                          type="number"
                          placeholder="8th"
                          value={newCommitmentData?.eighthInstallment || 0}
                          onChange={(e) => setNewCommitmentData({ ...newCommitmentData, eighthInstallment: Number(e.target.value) })}
                          fullWidth
                        />
                      </Box>
                      <Box sx={{ flex: '0 0 100px', minWidth: 100, p: 1 }}>
                        <TextField
                          size="small"
                          type="number"
                          placeholder="9th"
                          value={newCommitmentData?.ninthInstallment || 0}
                          onChange={(e) => setNewCommitmentData({ ...newCommitmentData, ninthInstallment: Number(e.target.value) })}
                          fullWidth
                        />
                      </Box>
                      <Box sx={{ flex: '0 0 100px', minWidth: 100, p: 1 }}>
                        <TextField
                          size="small"
                          type="number"
                          placeholder="10th"
                          value={newCommitmentData?.tenthInstallment || 0}
                          onChange={(e) => setNewCommitmentData({ ...newCommitmentData, tenthInstallment: Number(e.target.value) })}
                          fullWidth
                        />
                      </Box>
                      <Box sx={{ flex: '0 0 150px', minWidth: 150, p: 1 }}>
                        <TextField
                          size="small"
                          placeholder="Updated By"
                          value={newCommitmentData?.commitmentUpdatedBy || ''}
                          onChange={(e) => setNewCommitmentData({ ...newCommitmentData, commitmentUpdatedBy: e.target.value })}
                          fullWidth
                        />
                      </Box>
                      <Box sx={{ flex: '0 0 200px', minWidth: 200, p: 1 }}>
                        <TextField
                          size="small"
                          placeholder="Remarks"
                          value={newCommitmentData?.remarks || ''}
                          onChange={(e) => setNewCommitmentData({ ...newCommitmentData, remarks: e.target.value })}
                          fullWidth
                        />
                      </Box>
                      <Box sx={{ flex: '0 0 100px', minWidth: 100, p: 1, display: 'flex', gap: 0.5 }}>
                        <IconButton
                          size="small"
                          color="success"
                          onClick={handleSaveNewCommitment}
                          disabled={savingCommitment}
                          title="Save"
                        >
                          <SaveIcon fontSize="small" />
                        </IconButton>
                        <IconButton
                          size="small"
                          onClick={handleCancelAddCommitment}
                          title="Cancel"
                        >
                          <CancelIcon fontSize="small" />
                        </IconButton>
                      </Box>
                    </Box>
                  )}

                  {commitmentData.length > 0 ? (
                    commitmentData.map((row, index) => {
                      const isEditing = editingCommitmentId === row.documentId;

                      return (
                        <Box
                          key={row.documentId}
                          sx={{
                            display: 'flex',
                            borderBottom: '1px solid #e0e0e0',
                            fontSize: '0.875rem',
                            minWidth: 'min-content',
                            backgroundColor: index % 2 === 0 ? '#ffffff' : '#f9f9f9',
                            '&:hover': {
                              backgroundColor: isEditing ? 'transparent' : '#f9f9f9',
                            },
                          }}
                        >
                          <Box sx={{ flex: '0 0 60px', minWidth: 60, p: 1, display: 'flex', alignItems: 'center', overflow: 'hidden' }}>
                            {row.id}
                          </Box>
                          <Box sx={{ flex: '0 0 280px', minWidth: 280, p: 1, display: 'flex', alignItems: 'center', overflow: 'hidden' }}>
                            {isEditing ? (
                              <TextField
                                size="small"
                                value={editingCommitmentData?.name || ''}
                                onChange={(e) => handleChangeEditingCommitmentData('name', e.target.value)}
                                fullWidth
                                sx={{ fontSize: '0.875rem' }}
                              />
                            ) : (
                              row.name
                            )}
                          </Box>
                          <Box sx={{ flex: '0 0 120px', minWidth: 120, p: 1, display: 'flex', alignItems: 'center', justifyContent: 'flex-end', overflow: 'hidden' }}>
                            {isEditing ? (
                              <TextField
                                size="small"
                                type="number"
                                value={editingCommitmentData?.commitment || 0}
                                onChange={(e) => handleChangeEditingCommitmentData('commitment', Number(e.target.value))}
                                fullWidth
                                sx={{ fontSize: '0.875rem' }}
                              />
                            ) : (
                              row.commitment
                            )}
                          </Box>
                          <Box sx={{ flex: '0 0 120px', minWidth: 120, p: 1, display: 'flex', alignItems: 'center', justifyContent: 'flex-end', overflow: 'hidden' }}>
                            {isEditing ? (
                              <TextField
                                size="small"
                                type="number"
                                value={editingCommitmentData?.paid || 0}
                                onChange={(e) => handleChangeEditingCommitmentData('paid', Number(e.target.value))}
                                fullWidth
                                sx={{ fontSize: '0.875rem' }}
                              />
                            ) : (
                              row.paid
                            )}
                          </Box>
                          <Box sx={{ flex: '0 0 120px', minWidth: 120, p: 1, display: 'flex', alignItems: 'center', justifyContent: 'flex-end', overflow: 'hidden' }}>
                            {isEditing ? (
                              <TextField
                                size="small"
                                type="number"
                                value={editingCommitmentData?.due || 0}
                                onChange={(e) => handleChangeEditingCommitmentData('due', Number(e.target.value))}
                                fullWidth
                                sx={{ fontSize: '0.875rem' }}
                              />
                            ) : (
                              row.due
                            )}
                          </Box>
                          <Box sx={{ flex: '0 0 120px', minWidth: 120, p: 1, display: 'flex', alignItems: 'center', justifyContent: 'flex-end', overflow: 'hidden' }}>
                            {isEditing ? (
                              <TextField
                                size="small"
                                type="number"
                                value={editingCommitmentData?.firstInstallment || 0}
                                onChange={(e) => handleChangeEditingCommitmentData('firstInstallment', Number(e.target.value))}
                                fullWidth
                                sx={{ fontSize: '0.875rem' }}
                              />
                            ) : (
                              row.firstInstallment
                            )}
                          </Box>
                          <Box sx={{ flex: '0 0 120px', minWidth: 120, p: 1, display: 'flex', alignItems: 'center', justifyContent: 'flex-end', overflow: 'hidden' }}>
                            {isEditing ? (
                              <TextField
                                size="small"
                                type="number"
                                value={editingCommitmentData?.secondInstallment || 0}
                                onChange={(e) => handleChangeEditingCommitmentData('secondInstallment', Number(e.target.value))}
                                fullWidth
                                sx={{ fontSize: '0.875rem' }}
                              />
                            ) : (
                              row.secondInstallment
                            )}
                          </Box>
                          <Box sx={{ flex: '0 0 110px', minWidth: 110, p: 1, display: 'flex', alignItems: 'center', justifyContent: 'flex-end', overflow: 'hidden' }}>
                            {isEditing ? (
                              <TextField
                                size="small"
                                type="number"
                                value={editingCommitmentData?.thirdInstallment || 0}
                                onChange={(e) => handleChangeEditingCommitmentData('thirdInstallment', Number(e.target.value))}
                                fullWidth
                                sx={{ fontSize: '0.875rem' }}
                              />
                            ) : (
                              row.thirdInstallment
                            )}
                          </Box>
                          <Box sx={{ flex: '0 0 110px', minWidth: 110, p: 1, display: 'flex', alignItems: 'center', justifyContent: 'flex-end', overflow: 'hidden' }}>
                            {isEditing ? (
                              <TextField
                                size="small"
                                type="number"
                                value={editingCommitmentData?.fourthInstallment || 0}
                                onChange={(e) => handleChangeEditingCommitmentData('fourthInstallment', Number(e.target.value))}
                                fullWidth
                                sx={{ fontSize: '0.875rem' }}
                              />
                            ) : (
                              row.fourthInstallment
                            )}
                          </Box>
                          <Box sx={{ flex: '0 0 110px', minWidth: 110, p: 1, display: 'flex', alignItems: 'center', justifyContent: 'flex-end', overflow: 'hidden' }}>
                            {isEditing ? (
                              <TextField
                                size="small"
                                type="number"
                                value={editingCommitmentData?.fifthInstallment || 0}
                                onChange={(e) => handleChangeEditingCommitmentData('fifthInstallment', Number(e.target.value))}
                                fullWidth
                                sx={{ fontSize: '0.875rem' }}
                              />
                            ) : (
                              row.fifthInstallment
                            )}
                          </Box>
                          <Box sx={{ flex: '0 0 100px', minWidth: 100, p: 1, display: 'flex', alignItems: 'center', justifyContent: 'flex-end', overflow: 'hidden' }}>
                            {isEditing ? (
                              <TextField
                                size="small"
                                type="number"
                                value={editingCommitmentData?.sixthInstallment || 0}
                                onChange={(e) => handleChangeEditingCommitmentData('sixthInstallment', Number(e.target.value))}
                                fullWidth
                                sx={{ fontSize: '0.875rem' }}
                              />
                            ) : (
                              row.sixthInstallment
                            )}
                          </Box>
                          <Box sx={{ flex: '0 0 100px', minWidth: 100, p: 1, display: 'flex', alignItems: 'center', justifyContent: 'flex-end', overflow: 'hidden' }}>
                            {isEditing ? (
                              <TextField
                                size="small"
                                type="number"
                                value={editingCommitmentData?.seventhInstallment || 0}
                                onChange={(e) => handleChangeEditingCommitmentData('seventhInstallment', Number(e.target.value))}
                                fullWidth
                                sx={{ fontSize: '0.875rem' }}
                              />
                            ) : (
                              row.seventhInstallment
                            )}
                          </Box>
                          <Box sx={{ flex: '0 0 100px', minWidth: 100, p: 1, display: 'flex', alignItems: 'center', justifyContent: 'flex-end', overflow: 'hidden' }}>
                            {isEditing ? (
                              <TextField
                                size="small"
                                type="number"
                                value={editingCommitmentData?.eighthInstallment || 0}
                                onChange={(e) => handleChangeEditingCommitmentData('eighthInstallment', Number(e.target.value))}
                                fullWidth
                                sx={{ fontSize: '0.875rem' }}
                              />
                            ) : (
                              row.eighthInstallment
                            )}
                          </Box>
                          <Box sx={{ flex: '0 0 100px', minWidth: 100, p: 1, display: 'flex', alignItems: 'center', justifyContent: 'flex-end', overflow: 'hidden' }}>
                            {isEditing ? (
                              <TextField
                                size="small"
                                type="number"
                                value={editingCommitmentData?.ninthInstallment || 0}
                                onChange={(e) => handleChangeEditingCommitmentData('ninthInstallment', Number(e.target.value))}
                                fullWidth
                                sx={{ fontSize: '0.875rem' }}
                              />
                            ) : (
                              row.ninthInstallment
                            )}
                          </Box>
                          <Box sx={{ flex: '0 0 100px', minWidth: 100, p: 1, display: 'flex', alignItems: 'center', justifyContent: 'flex-end', overflow: 'hidden' }}>
                            {isEditing ? (
                              <TextField
                                size="small"
                                type="number"
                                value={editingCommitmentData?.tenthInstallment || 0}
                                onChange={(e) => handleChangeEditingCommitmentData('tenthInstallment', Number(e.target.value))}
                                fullWidth
                                sx={{ fontSize: '0.875rem' }}
                              />
                            ) : (
                              row.tenthInstallment
                            )}
                          </Box>
                          <Box sx={{ flex: '0 0 150px', minWidth: 150, p: 1, display: 'flex', alignItems: 'center', overflow: 'hidden' }}>
                            {isEditing ? (
                              <TextField
                                size="small"
                                value={editingCommitmentData?.commitmentUpdatedBy || ''}
                                onChange={(e) => handleChangeEditingCommitmentData('commitmentUpdatedBy', e.target.value)}
                                fullWidth
                                sx={{ fontSize: '0.875rem' }}
                              />
                            ) : (
                              row.commitmentUpdatedBy
                            )}
                          </Box>
                          <Box sx={{ flex: '0 0 200px', minWidth: 200, p: 1, display: 'flex', alignItems: 'center', overflow: 'hidden' }}>
                            {isEditing ? (
                              <TextField
                                size="small"
                                value={editingCommitmentData?.remarks || ''}
                                onChange={(e) => handleChangeEditingCommitmentData('remarks', e.target.value)}
                                fullWidth
                                sx={{ fontSize: '0.875rem' }}
                              />
                            ) : (
                              row.remarks
                            )}
                          </Box>
                          <Box sx={{ flex: '0 0 100px', minWidth: 100, p: 1, display: 'flex', alignItems: 'center', gap: 0.5 }}>
                            {isMaintainer && !isEditing && (
                              <>
                                <IconButton
                                  size="small"
                                  onClick={() => handleEditCommitment(row)}
                                  title="Edit"
                                >
                                  <EditIcon fontSize="small" />
                                </IconButton>
                                <IconButton
                                  size="small"
                                  onClick={() => {
                                    if (window.confirm('Are you sure you want to delete this commitment record?')) {
                                      handleDeleteCommitment(row.documentId);
                                    }
                                  }}
                                  disabled={savingCommitment}
                                  title="Delete"
                                >
                                  <DeleteIcon fontSize="small" />
                                </IconButton>
                              </>
                            )}
                            {isMaintainer && isEditing && (
                              <>
                                <IconButton
                                  size="small"
                                  onClick={handleSaveCommitment}
                                  disabled={savingCommitment}
                                  title="Save"
                                >
                                  <SaveIcon fontSize="small" />
                                </IconButton>
                                <IconButton
                                  size="small"
                                  onClick={handleCancelCommitment}
                                  title="Cancel"
                                >
                                  <CancelIcon fontSize="small" />
                                </IconButton>
                              </>
                            )}
                          </Box>
                        </Box>
                      );
                    })
                  ) : (
                    <Box sx={{ p: 3, textAlign: 'center', color: '#666', minWidth: '100%' }}>
                      No commitment records found
                    </Box>
                  )}
                </Box>
              </Box>
            )}
          </TabPanel>

          <TabPanel value={tabValue} index={2}>
            {loading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 400 }}>
                <CircularProgress />
              </Box>
            ) : (
              <Box sx={{ border: '1px solid #e0e0e0', borderRadius: 1, overflow: 'hidden', maxHeight: 700, display: 'flex', flexDirection: 'column' }}>
                {/* Single scrollable container for both header and body */}
                <Box
                  sx={{
                    overflowY: 'auto',
                    overflowX: 'auto',
                    flex: 1,
                    display: 'flex',
                    flexDirection: 'column',
                  }}
                >
                  {/* Table Header with Add Button */}
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', p: { xs: 0.75, md: 1 }, backgroundColor: '#fff', borderBottom: '1px solid #e0e0e0', gap: 1 }}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 'bold', fontSize: { xs: '0.813rem', md: '1rem' }, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      Savasin ({savasinData.length})
                    </Typography>
                    {isMaintainer && (
                      <Tooltip title="Add new savasin record">
                        <IconButton
                          size="small"
                          color="primary"
                          onClick={handleAddSavasinRow}
                          disabled={addingSavasin}
                        >
                          <AddIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    )}
                  </Box>

                  {/* Table Column Headers - Sticky to top, scrolls horizontally with body */}
                  <Box
                    sx={{
                      display: 'flex',
                      backgroundColor: '#f5f5f5',
                      borderBottom: '2px solid #e0e0e0',
                      fontWeight: 'bold',
                      fontSize: '0.875rem',
                      position: 'sticky',
                      top: 0,
                      zIndex: 5,
                      minWidth: 'min-content',
                      flexShrink: 0,
                    }}
                  >
                    <Box sx={{ flex: '0 0 60px', minWidth: 60, p: 1 }}>ID</Box>
                    <Box sx={{ flex: '0 0 180px', minWidth: 180, p: 1 }}>Name</Box>
                    <Box sx={{ flex: '0 0 200px', minWidth: 200, p: 1 }}>Family Name</Box>
                    <Box sx={{ flex: '0 0 150px', minWidth: 150, p: 1 }}>Contact Number</Box>
                    <Box sx={{ flex: '0 0 120px', minWidth: 120, p: 1, textAlign: 'right' }}>Commitment</Box>
                    <Box sx={{ flex: '0 0 100px', minWidth: 100, p: 1, textAlign: 'right' }}>Paid</Box>
                    <Box sx={{ flex: '0 0 100px', minWidth: 100, p: 1, textAlign: 'right' }}>Due</Box>
                    <Box sx={{ flex: '0 0 250px', minWidth: 250, p: 1 }}>Remarks</Box>
                    <Box sx={{ flex: '0 0 100px', minWidth: 100, p: 1 }}>Actions</Box>
                  </Box>

                  {/* New Savasin Row Form */}
                  {addingSavasin && (
                    <Box
                      sx={{
                        display: 'flex',
                        borderBottom: '2px solid #1976d2',
                        backgroundColor: '#e3f2fd',
                        fontSize: '0.875rem',
                        minWidth: 'min-content',
                      }}
                    >
                      <Box sx={{ flex: '0 0 60px', minWidth: 60, p: 1 }}>-</Box>
                      <Box sx={{ flex: '0 0 180px', minWidth: 180, p: 1 }}>
                        <TextField
                          size="small"
                          placeholder="Name"
                          value={newSavasinData?.name || ''}
                          onChange={(e) => setNewSavasinData({ ...newSavasinData, name: e.target.value })}
                          fullWidth
                        />
                      </Box>
                      <Box sx={{ flex: '0 0 200px', minWidth: 200, p: 1 }}>
                        <TextField
                          size="small"
                          placeholder="Family Name"
                          value={newSavasinData?.familyName || ''}
                          onChange={(e) => setNewSavasinData({ ...newSavasinData, familyName: e.target.value })}
                          fullWidth
                        />
                      </Box>
                      <Box sx={{ flex: '0 0 150px', minWidth: 150, p: 1 }}>
                        <TextField
                          size="small"
                          placeholder="Contact"
                          value={newSavasinData?.contactNumber || ''}
                          onChange={(e) => setNewSavasinData({ ...newSavasinData, contactNumber: e.target.value })}
                          fullWidth
                        />
                      </Box>
                      <Box sx={{ flex: '0 0 120px', minWidth: 120, p: 1 }}>
                        <TextField
                          size="small"
                          type="number"
                          placeholder="Commitment"
                          value={newSavasinData?.commitment || 0}
                          onChange={(e) => setNewSavasinData({ ...newSavasinData, commitment: Number(e.target.value) })}
                          fullWidth
                        />
                      </Box>
                      <Box sx={{ flex: '0 0 100px', minWidth: 100, p: 1 }}>
                        <TextField
                          size="small"
                          type="number"
                          placeholder="Paid"
                          value={newSavasinData?.paid || 0}
                          onChange={(e) => setNewSavasinData({ ...newSavasinData, paid: Number(e.target.value) })}
                          fullWidth
                        />
                      </Box>
                      <Box sx={{ flex: '0 0 100px', minWidth: 100, p: 1 }}>
                        <TextField
                          size="small"
                          type="number"
                          placeholder="Due"
                          value={newSavasinData?.due || 0}
                          onChange={(e) => setNewSavasinData({ ...newSavasinData, due: Number(e.target.value) })}
                          fullWidth
                        />
                      </Box>
                      <Box sx={{ flex: '0 0 250px', minWidth: 250, p: 1 }}>
                        <TextField
                          size="small"
                          placeholder="Remarks"
                          value={newSavasinData?.remarks || ''}
                          onChange={(e) => setNewSavasinData({ ...newSavasinData, remarks: e.target.value })}
                          fullWidth
                        />
                      </Box>
                      <Box sx={{ flex: '0 0 100px', minWidth: 100, p: 1, display: 'flex', gap: 0.5 }}>
                        <IconButton
                          size="small"
                          color="success"
                          onClick={handleSaveNewSavasin}
                          disabled={savingSavasin}
                          title="Save"
                        >
                          <SaveIcon fontSize="small" />
                        </IconButton>
                        <IconButton
                          size="small"
                          onClick={handleCancelAddSavasin}
                          title="Cancel"
                        >
                          <CancelIcon fontSize="small" />
                        </IconButton>
                      </Box>
                    </Box>
                  )}

                  {/* Table Body Rows */}
                  {savasinData.length > 0 ? (
                    savasinData.map((row, index) => {
                      const isEditing = editingSavasinId === row.documentId;

                      return (
                        <Box
                          key={row.documentId}
                          sx={{
                            display: 'flex',
                            borderBottom: '1px solid #e0e0e0',
                            fontSize: '0.875rem',
                            minWidth: 'min-content',
                            backgroundColor: index % 2 === 0 ? '#ffffff' : '#f9f9f9',
                            '&:hover': {
                              backgroundColor: isEditing ? 'transparent' : '#f9f9f9',
                            },
                          }}
                        >
                          <Box sx={{ flex: '0 0 60px', minWidth: 60, p: 1, display: 'flex', alignItems: 'center', overflow: 'hidden' }}>
                            {row.id}
                          </Box>
                          <Box sx={{ flex: '0 0 180px', minWidth: 180, p: 1, display: 'flex', alignItems: 'center', overflow: 'hidden' }}>
                            {isEditing ? (
                              <TextField
                                size="small"
                                value={editingSavasinData?.name || ''}
                                onChange={(e) => handleChangeSavasinData('name', e.target.value)}
                                fullWidth
                                sx={{ fontSize: '0.875rem' }}
                              />
                            ) : (
                              row.name
                            )}
                          </Box>
                          <Box sx={{ flex: '0 0 200px', minWidth: 200, p: 1, display: 'flex', alignItems: 'center', overflow: 'hidden' }}>
                            {isEditing ? (
                              <TextField
                                size="small"
                                value={editingSavasinData?.familyName || ''}
                                onChange={(e) => handleChangeSavasinData('familyName', e.target.value)}
                                fullWidth
                                sx={{ fontSize: '0.875rem' }}
                              />
                            ) : (
                              row.familyName
                            )}
                          </Box>
                          <Box sx={{ flex: '0 0 150px', minWidth: 150, p: 1, display: 'flex', alignItems: 'center', overflow: 'hidden' }}>
                            {isEditing ? (
                              <TextField
                                size="small"
                                value={editingSavasinData?.contactNumber || ''}
                                onChange={(e) => handleChangeSavasinData('contactNumber', e.target.value)}
                                fullWidth
                                sx={{ fontSize: '0.875rem' }}
                              />
                            ) : (
                              row.contactNumber
                            )}
                          </Box>
                          <Box sx={{ flex: '0 0 120px', minWidth: 120, p: 1, display: 'flex', alignItems: 'center', justifyContent: 'flex-end', overflow: 'hidden' }}>
                            {isEditing ? (
                              <TextField
                                size="small"
                                type="number"
                                value={editingSavasinData?.commitment || 0}
                                onChange={(e) => handleChangeSavasinData('commitment', Number(e.target.value))}
                                fullWidth
                                sx={{ fontSize: '0.875rem' }}
                              />
                            ) : (
                              row.commitment
                            )}
                          </Box>
                          <Box sx={{ flex: '0 0 100px', minWidth: 100, p: 1, display: 'flex', alignItems: 'center', justifyContent: 'flex-end', overflow: 'hidden' }}>
                            {isEditing ? (
                              <TextField
                                size="small"
                                type="number"
                                value={editingSavasinData?.paid || 0}
                                onChange={(e) => handleChangeSavasinData('paid', Number(e.target.value))}
                                fullWidth
                                sx={{ fontSize: '0.875rem' }}
                              />
                            ) : (
                              row.paid
                            )}
                          </Box>
                          <Box sx={{ flex: '0 0 100px', minWidth: 100, p: 1, display: 'flex', alignItems: 'center', justifyContent: 'flex-end', overflow: 'hidden' }}>
                            {isEditing ? (
                              <TextField
                                size="small"
                                type="number"
                                value={editingSavasinData?.due || 0}
                                onChange={(e) => handleChangeSavasinData('due', Number(e.target.value))}
                                fullWidth
                                sx={{ fontSize: '0.875rem' }}
                              />
                            ) : (
                              row.due
                            )}
                          </Box>
                          <Box sx={{ flex: '0 0 250px', minWidth: 250, p: 1, display: 'flex', alignItems: 'center', overflow: 'hidden' }}>
                            {isEditing ? (
                              <TextField
                                size="small"
                                value={editingSavasinData?.remarks || ''}
                                onChange={(e) => handleChangeSavasinData('remarks', e.target.value)}
                                fullWidth
                                sx={{ fontSize: '0.875rem' }}
                              />
                            ) : (
                              row.remarks
                            )}
                          </Box>
                          <Box sx={{ flex: '0 0 100px', minWidth: 100, p: 1, display: 'flex', alignItems: 'center', gap: 0.5 }}>
                            {isMaintainer && !isEditing && (
                              <>
                                <IconButton
                                  size="small"
                                  onClick={() => handleEditSavasin(row)}
                                  title="Edit"
                                >
                                  <EditIcon fontSize="small" />
                                </IconButton>
                                <IconButton
                                  size="small"
                                  onClick={() => {
                                    if (window.confirm('Are you sure you want to delete this savasin record?')) {
                                      handleDeleteSavasin(row.documentId);
                                    }
                                  }}
                                  disabled={savingSavasin}
                                  title="Delete"
                                >
                                  <DeleteIcon fontSize="small" />
                                </IconButton>
                              </>
                            )}
                            {isMaintainer && isEditing && (
                              <>
                                <IconButton
                                  size="small"
                                  onClick={handleSaveSavasin}
                                  disabled={savingSavasin}
                                  title="Save"
                                >
                                  <SaveIcon fontSize="small" />
                                </IconButton>
                                <IconButton
                                  size="small"
                                  onClick={handleCancelSavasin}
                                  title="Cancel"
                                >
                                  <CancelIcon fontSize="small" />
                                </IconButton>
                              </>
                            )}
                          </Box>
                        </Box>
                      );
                    })
                  ) : (
                    <Box sx={{ p: 3, textAlign: 'center', color: '#666', minWidth: '100%' }}>
                      No savasin records found
                    </Box>
                  )}
                </Box>
              </Box>
            )}
          </TabPanel>

          <TabPanel value={tabValue} index={3}>
            {loading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 400 }}>
                <CircularProgress />
              </Box>
            ) : (
              <Box sx={{ border: '1px solid #e0e0e0', borderRadius: 1, overflow: 'hidden', maxHeight: 700, display: 'flex', flexDirection: 'column' }}>
                <Box
                  sx={{
                    overflowY: 'auto',
                    overflowX: 'auto',
                    flex: 1,
                    display: 'flex',
                    flexDirection: 'column',
                  }}
                >
                  {/* Table Header with Add Button */}
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', p: { xs: 0.75, md: 1 }, backgroundColor: '#fff', borderBottom: '1px solid #e0e0e0', gap: 1 }}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 'bold', fontSize: { xs: '0.813rem', md: '1rem' }, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      Other Sources ({otherSourcesData.length})
                    </Typography>
                    {isMaintainer && (
                      <Tooltip title="Add new other source record">
                        <IconButton
                          size="small"
                          color="primary"
                          onClick={handleAddOtherSourceRow}
                          disabled={addingOtherSource}
                        >
                          <AddIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    )}
                  </Box>

                  {/* Table Column Headers */}
                  <Box
                    sx={{
                      display: 'flex',
                      backgroundColor: '#f5f5f5',
                      borderBottom: '2px solid #e0e0e0',
                      fontWeight: 'bold',
                      fontSize: '0.875rem',
                      position: 'sticky',
                      top: 0,
                      zIndex: 5,
                      minWidth: 'min-content',
                      flexShrink: 0,
                    }}
                  >
                    <Box sx={{ flex: '0 0 70px', minWidth: 70, p: 1 }}>Sl No</Box>
                    <Box sx={{ flex: '0 0 250px', minWidth: 250, p: 1 }}>Item</Box>
                    <Box sx={{ flex: '0 0 150px', minWidth: 150, p: 1 }}>Date</Box>
                    <Box sx={{ flex: '0 0 150px', minWidth: 150, p: 1, textAlign: 'right' }}>Amount</Box>
                    <Box sx={{ flex: '0 0 300px', minWidth: 300, p: 1 }}>Comment</Box>
                    <Box sx={{ flex: '0 0 180px', minWidth: 180, p: 1 }}>Attachment</Box>
                    <Box sx={{ flex: '0 0 100px', minWidth: 100, p: 1 }}>Actions</Box>
                  </Box>

                  {/* New Other Source Row Form */}
                  {addingOtherSource && (
                    <Box
                      sx={{
                        display: 'flex',
                        borderBottom: '2px solid #1976d2',
                        backgroundColor: '#e3f2fd',
                        fontSize: '0.875rem',
                        minWidth: 'min-content',
                      }}
                    >
                      <Box sx={{ flex: '0 0 70px', minWidth: 70, p: 1 }}>-</Box>
                      <Box sx={{ flex: '0 0 250px', minWidth: 250, p: 1 }}>
                        <TextField
                          size="small"
                          placeholder="Item"
                          value={newOtherSourceData?.Item || ''}
                          onChange={(e) => setNewOtherSourceData({ ...newOtherSourceData, Item: e.target.value })}
                          fullWidth
                        />
                      </Box>
                      <Box sx={{ flex: '0 0 150px', minWidth: 150, p: 1 }}>
                        <TextField
                          size="small"
                          type="date"
                          value={newOtherSourceData?.Date || ''}
                          onChange={(e) => setNewOtherSourceData({ ...newOtherSourceData, Date: e.target.value })}
                          fullWidth
                          InputLabelProps={{ shrink: true }}
                        />
                      </Box>
                      <Box sx={{ flex: '0 0 150px', minWidth: 150, p: 1 }}>
                        <TextField
                          size="small"
                          type="number"
                          placeholder="Amount"
                          value={newOtherSourceData?.Amount || 0}
                          onChange={(e) => setNewOtherSourceData({ ...newOtherSourceData, Amount: Number(e.target.value) })}
                          fullWidth
                        />
                      </Box>
                      <Box sx={{ flex: '0 0 300px', minWidth: 300, p: 1 }}>
                        <TextField
                          size="small"
                          placeholder="Comment"
                          value={newOtherSourceData?.Comment || ''}
                          onChange={(e) => setNewOtherSourceData({ ...newOtherSourceData, Comment: e.target.value })}
                          fullWidth
                        />
                      </Box>
                      <Box sx={{ flex: '0 0 180px', minWidth: 180, p: 1 }}>
                        <input
                          type="file"
                          ref={newOtherSourceFileRef}
                          style={{ display: 'none' }}
                          onChange={(e: React.ChangeEvent<HTMLInputElement>) => setNewOtherSourceFile(e.target.files?.[0] || null)}
                        />
                        <Button
                          size="small"
                          variant="outlined"
                          startIcon={<CloudUploadIcon />}
                          onClick={() => newOtherSourceFileRef.current?.click()}
                          sx={{ fontSize: '0.7rem', textTransform: 'none' }}
                        >
                          {newOtherSourceFile ? newOtherSourceFile.name : 'Upload'}
                        </Button>
                      </Box>
                      <Box sx={{ flex: '0 0 100px', minWidth: 100, p: 1, display: 'flex', gap: 0.5 }}>
                        <IconButton
                          size="small"
                          color="success"
                          onClick={handleSaveNewOtherSource}
                          disabled={savingOtherSource}
                          title="Save"
                        >
                          <SaveIcon fontSize="small" />
                        </IconButton>
                        <IconButton
                          size="small"
                          onClick={handleCancelAddOtherSource}
                          title="Cancel"
                        >
                          <CancelIcon fontSize="small" />
                        </IconButton>
                      </Box>
                    </Box>
                  )}

                  {/* Table Body Rows */}
                  {otherSourcesData.length > 0 ? (
                    otherSourcesData.map((row, index) => {
                      const isEditing = editingOtherSourceId === row.documentId;

                      return (
                        <Box
                          key={row.documentId}
                          sx={{
                            display: 'flex',
                            borderBottom: '1px solid #e0e0e0',
                            fontSize: '0.875rem',
                            minWidth: 'min-content',
                            backgroundColor: index % 2 === 0 ? '#ffffff' : '#f9f9f9',
                            '&:hover': {
                              backgroundColor: isEditing ? 'transparent' : '#f9f9f9',
                            },
                          }}
                        >
                          <Box sx={{ flex: '0 0 70px', minWidth: 70, p: 1, display: 'flex', alignItems: 'center', overflow: 'hidden' }}>
                            {index + 1}
                          </Box>
                          <Box sx={{ flex: '0 0 250px', minWidth: 250, p: 1, display: 'flex', alignItems: 'center', overflow: 'hidden' }}>
                            {isEditing ? (
                              <TextField
                                size="small"
                                value={editingOtherSourceData?.Item || ''}
                                onChange={(e) => handleChangeOtherSourceData('Item', e.target.value)}
                                fullWidth
                                sx={{ fontSize: '0.875rem' }}
                              />
                            ) : (
                              row.Item
                            )}
                          </Box>
                          <Box sx={{ flex: '0 0 150px', minWidth: 150, p: 1, display: 'flex', alignItems: 'center', overflow: 'hidden' }}>
                            {isEditing ? (
                              <TextField
                                size="small"
                                type="date"
                                value={editingOtherSourceData?.Date || ''}
                                onChange={(e) => handleChangeOtherSourceData('Date', e.target.value)}
                                fullWidth
                                sx={{ fontSize: '0.875rem' }}
                                InputLabelProps={{ shrink: true }}
                              />
                            ) : (
                              row.Date
                            )}
                          </Box>
                          <Box sx={{ flex: '0 0 150px', minWidth: 150, p: 1, display: 'flex', alignItems: 'center', justifyContent: 'flex-end', overflow: 'hidden' }}>
                            {isEditing ? (
                              <TextField
                                size="small"
                                type="number"
                                value={editingOtherSourceData?.Amount || 0}
                                onChange={(e) => handleChangeOtherSourceData('Amount', Number(e.target.value))}
                                fullWidth
                                sx={{ fontSize: '0.875rem' }}
                              />
                            ) : (
                              row.Amount
                            )}
                          </Box>
                          <Box sx={{ flex: '0 0 300px', minWidth: 300, p: 1, display: 'flex', alignItems: 'center', overflow: 'hidden' }}>
                            {isEditing ? (
                              <TextField
                                size="small"
                                value={editingOtherSourceData?.Comment || ''}
                                onChange={(e) => handleChangeOtherSourceData('Comment', e.target.value)}
                                fullWidth
                                sx={{ fontSize: '0.875rem' }}
                              />
                            ) : (
                              row.Comment
                            )}
                          </Box>
                          <Box sx={{ flex: '0 0 180px', minWidth: 180, p: 1, display: 'flex', alignItems: 'center', overflow: 'hidden' }}>
                            {isEditing ? (
                              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, width: '100%' }}>
                                {/* Show existing attachment if not removing */}
                                {editingOtherSourceData?.attachment && !removeEditAttachment && !editOtherSourceFile && (
                                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                    <AttachFileIcon sx={{ fontSize: '0.875rem', color: '#1976d2' }} />
                                    <Typography variant="caption" sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: 80 }}>
                                      {editingOtherSourceData.attachment.name}
                                    </Typography>
                                    <IconButton size="small" onClick={() => setRemoveEditAttachment(true)} title="Remove" sx={{ p: 0.25 }}>
                                      <DeleteIcon sx={{ fontSize: '0.75rem' }} />
                                    </IconButton>
                                  </Box>
                                )}
                                {editOtherSourceFile && (
                                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                    <Typography variant="caption" sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: 100 }}>
                                      {editOtherSourceFile.name}
                                    </Typography>
                                    <IconButton size="small" onClick={() => setEditOtherSourceFile(null)} title="Remove" sx={{ p: 0.25 }}>
                                      <CancelIcon sx={{ fontSize: '0.75rem' }} />
                                    </IconButton>
                                  </Box>
                                )}
                                <input
                                  type="file"
                                  ref={editOtherSourceFileRef}
                                  style={{ display: 'none' }}
                                  onChange={(e) => {
                                    setEditOtherSourceFile(e.target.files?.[0] || null);
                                    setRemoveEditAttachment(false);
                                  }}
                                />
                                <Button
                                  size="small"
                                  variant="outlined"
                                  startIcon={<CloudUploadIcon />}
                                  onClick={() => editOtherSourceFileRef.current?.click()}
                                  sx={{ fontSize: '0.65rem', textTransform: 'none', py: 0.25 }}
                                >
                                  {editOtherSourceFile || (editingOtherSourceData?.attachment && !removeEditAttachment) ? 'Replace' : 'Upload'}
                                </Button>
                              </Box>
                            ) : (
                              row.attachment ? (
                                row.attachment.mime?.startsWith('image/') ? (
                                  <Box
                                    component="img"
                                    src={`${API_BASE_URL}${row.attachment.url}`}
                                    alt={row.attachment.name}
                                    sx={{
                                      width: 50,
                                      height: 50,
                                      borderRadius: 1,
                                      objectFit: 'cover',
                                      border: '1px solid #ddd',
                                      cursor: 'pointer',
                                      '&:hover': { opacity: 0.8, transform: 'scale(1.05)' },
                                      transition: 'all 0.2s',
                                    }}
                                    onClick={() => {
                                      setSelectedOtherSourceAttachment(row.attachment);
                                      setOtherSourceViewerOpen(true);
                                    }}
                                    title={row.attachment.name}
                                  />
                                ) : (
                                  <MuiLink
                                    component="button"
                                    onClick={() => {
                                      setSelectedOtherSourceAttachment(row.attachment);
                                      setOtherSourceViewerOpen(true);
                                    }}
                                    sx={{ display: 'flex', alignItems: 'center', gap: 0.5, fontSize: '0.813rem', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', cursor: 'pointer' }}
                                    title={row.attachment.name}
                                  >
                                    <AttachFileIcon sx={{ fontSize: '1rem' }} />
                                    {row.attachment.name}
                                  </MuiLink>
                                )
                              ) : (
                                <Typography variant="caption" color="textSecondary">—</Typography>
                              )
                            )}
                          </Box>
                          <Box sx={{ flex: '0 0 100px', minWidth: 100, p: 1, display: 'flex', alignItems: 'center', gap: 0.5 }}>
                            {isMaintainer && !isEditing && (
                              <>
                                <IconButton
                                  size="small"
                                  onClick={() => handleEditOtherSource(row)}
                                  title="Edit"
                                >
                                  <EditIcon fontSize="small" />
                                </IconButton>
                                <IconButton
                                  size="small"
                                  onClick={() => {
                                    if (window.confirm('Are you sure you want to delete this record?')) {
                                      handleDeleteOtherSource(row.documentId);
                                    }
                                  }}
                                  disabled={savingOtherSource}
                                  title="Delete"
                                >
                                  <DeleteIcon fontSize="small" />
                                </IconButton>
                              </>
                            )}
                            {isMaintainer && isEditing && (
                              <>
                                <IconButton
                                  size="small"
                                  onClick={handleSaveOtherSource}
                                  disabled={savingOtherSource}
                                  title="Save"
                                >
                                  <SaveIcon fontSize="small" />
                                </IconButton>
                                <IconButton
                                  size="small"
                                  onClick={handleCancelOtherSource}
                                  title="Cancel"
                                >
                                  <CancelIcon fontSize="small" />
                                </IconButton>
                              </>
                            )}
                          </Box>
                        </Box>
                      );
                    })
                  ) : (
                    <Box sx={{ p: 3, textAlign: 'center', color: '#666', minWidth: '100%' }}>
                      No other source records found
                    </Box>
                  )}
                </Box>
              </Box>
            )}
          </TabPanel>

          <TabPanel value={tabValue} index={4}>
            {loading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 400 }}>
                <CircularProgress />
              </Box>
            ) : (
              <Box sx={{ border: '1px solid #e0e0e0', borderRadius: 1, overflow: 'hidden', maxHeight: 700, display: 'flex', flexDirection: 'column' }}>
                {/* Single scrollable container for both header and body */}
                <Box
                  sx={{
                    overflowY: 'auto',
                    overflowX: 'auto',
                    flex: 1,
                    display: 'flex',
                    flexDirection: 'column',
                  }}
                >
                  {/* Table Header with Add Button */}
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', p: { xs: 0.75, md: 1 }, backgroundColor: '#fff', borderBottom: '1px solid #e0e0e0', gap: 1 }}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 'bold', fontSize: { xs: '0.813rem', md: '1rem' }, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      Members ({membersData.length})
                    </Typography>
                    {isMaintainer && (
                      <Tooltip title="Add new member record">
                        <IconButton
                          size="small"
                          color="primary"
                          onClick={handleAddMemberRow}
                          disabled={addingMember}
                        >
                          <AddIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    )}
                  </Box>

                  {/* Table Column Headers - Sticky to top, scrolls horizontally with body */}
                  <Box
                    sx={{
                      display: 'flex',
                      backgroundColor: '#f5f5f5',
                      borderBottom: '2px solid #e0e0e0',
                      fontWeight: 'bold',
                      fontSize: '0.875rem',
                      position: 'sticky',
                      top: 0,
                      zIndex: 5,
                      minWidth: 'min-content',
                      flexShrink: 0,
                    }}
                  >
                    <Box sx={{ flex: '0 0 60px', minWidth: 60, p: 1 }}>ID</Box>
                    <Box sx={{ flex: '0 0 180px', minWidth: 180, p: 1 }}>Name</Box>
                    <Box sx={{ flex: '0 0 200px', minWidth: 200, p: 1 }}>Family Name</Box>
                    <Box sx={{ flex: '0 0 150px', minWidth: 150, p: 1 }}>Contact Number</Box>
                    <Box sx={{ flex: '0 0 120px', minWidth: 120, p: 1, textAlign: 'right' }}>Commitment</Box>
                    <Box sx={{ flex: '0 0 100px', minWidth: 100, p: 1, textAlign: 'right' }}>Paid</Box>
                    <Box sx={{ flex: '0 0 100px', minWidth: 100, p: 1, textAlign: 'right' }}>Due</Box>
                    <Box sx={{ flex: '0 0 250px', minWidth: 250, p: 1 }}>Remarks</Box>
                    <Box sx={{ flex: '0 0 100px', minWidth: 100, p: 1 }}>Actions</Box>
                  </Box>

                  {/* New Member Row Form */}
                  {addingMember && (
                    <Box
                      sx={{
                        display: 'flex',
                        borderBottom: '2px solid #1976d2',
                        backgroundColor: '#e3f2fd',
                        fontSize: '0.875rem',
                        minWidth: 'min-content',
                      }}
                    >
                      <Box sx={{ flex: '0 0 60px', minWidth: 60, p: 1 }}>-</Box>
                      <Box sx={{ flex: '0 0 180px', minWidth: 180, p: 1 }}>
                        <TextField
                          size="small"
                          placeholder="Name"
                          value={newMemberData?.name || ''}
                          onChange={(e) => setNewMemberData({ ...newMemberData, name: e.target.value })}
                          fullWidth
                        />
                      </Box>
                      <Box sx={{ flex: '0 0 200px', minWidth: 200, p: 1 }}>
                        <TextField
                          size="small"
                          placeholder="Family Name"
                          value={newMemberData?.familyName || ''}
                          onChange={(e) => setNewMemberData({ ...newMemberData, familyName: e.target.value })}
                          fullWidth
                        />
                      </Box>
                      <Box sx={{ flex: '0 0 150px', minWidth: 150, p: 1 }}>
                        <TextField
                          size="small"
                          placeholder="Contact"
                          value={newMemberData?.contactNumber || ''}
                          onChange={(e) => setNewMemberData({ ...newMemberData, contactNumber: e.target.value })}
                          fullWidth
                        />
                      </Box>
                      <Box sx={{ flex: '0 0 120px', minWidth: 120, p: 1 }}>
                        <TextField
                          size="small"
                          type="number"
                          placeholder="Commitment"
                          value={newMemberData?.commitment || 0}
                          onChange={(e) => setNewMemberData({ ...newMemberData, commitment: Number(e.target.value) })}
                          fullWidth
                        />
                      </Box>
                      <Box sx={{ flex: '0 0 100px', minWidth: 100, p: 1 }}>
                        <TextField
                          size="small"
                          type="number"
                          placeholder="Paid"
                          value={newMemberData?.paid || 0}
                          onChange={(e) => setNewMemberData({ ...newMemberData, paid: Number(e.target.value) })}
                          fullWidth
                        />
                      </Box>
                      <Box sx={{ flex: '0 0 100px', minWidth: 100, p: 1 }}>
                        <TextField
                          size="small"
                          type="number"
                          placeholder="Due"
                          value={newMemberData?.due || 0}
                          onChange={(e) => setNewMemberData({ ...newMemberData, due: Number(e.target.value) })}
                          fullWidth
                        />
                      </Box>
                      <Box sx={{ flex: '0 0 250px', minWidth: 250, p: 1 }}>
                        <TextField
                          size="small"
                          placeholder="Remarks"
                          value={newMemberData?.remarks || ''}
                          onChange={(e) => setNewMemberData({ ...newMemberData, remarks: e.target.value })}
                          fullWidth
                        />
                      </Box>
                      <Box sx={{ flex: '0 0 100px', minWidth: 100, p: 1, display: 'flex', gap: 0.5 }}>
                        <IconButton
                          size="small"
                          color="success"
                          onClick={handleSaveNewMember}
                          disabled={savingMember}
                          title="Save"
                        >
                          <SaveIcon fontSize="small" />
                        </IconButton>
                        <IconButton
                          size="small"
                          onClick={handleCancelAddMember}
                          title="Cancel"
                        >
                          <CancelIcon fontSize="small" />
                        </IconButton>
                      </Box>
                    </Box>
                  )}

                  {/* Table Body Rows */}
                  {membersData.length > 0 ? (
                    membersData.map((row, index) => {
                      const isEditing = editingMemberId === row.documentId;

                      return (
                        <Box
                          key={row.documentId}
                          sx={{
                            display: 'flex',
                            borderBottom: '1px solid #e0e0e0',
                            fontSize: '0.875rem',
                            minWidth: 'min-content',
                            backgroundColor: index % 2 === 0 ? '#ffffff' : '#f9f9f9',
                            '&:hover': {
                              backgroundColor: isEditing ? 'transparent' : '#f9f9f9',
                            },
                          }}
                        >
                          <Box sx={{ flex: '0 0 60px', minWidth: 60, p: 1, display: 'flex', alignItems: 'center', overflow: 'hidden' }}>
                            {row.id}
                          </Box>
                          <Box sx={{ flex: '0 0 180px', minWidth: 180, p: 1, display: 'flex', alignItems: 'center', overflow: 'hidden' }}>
                            {isEditing ? (
                              <TextField
                                size="small"
                                value={editingMemberData?.name || ''}
                                onChange={(e) => handleChangeMemberData('name', e.target.value)}
                                fullWidth
                                sx={{ fontSize: '0.875rem' }}
                              />
                            ) : (
                              row.name
                            )}
                          </Box>
                          <Box sx={{ flex: '0 0 200px', minWidth: 200, p: 1, display: 'flex', alignItems: 'center', overflow: 'hidden' }}>
                            {isEditing ? (
                              <TextField
                                size="small"
                                value={editingMemberData?.familyName || ''}
                                onChange={(e) => handleChangeMemberData('familyName', e.target.value)}
                                fullWidth
                                sx={{ fontSize: '0.875rem' }}
                              />
                            ) : (
                              row.familyName
                            )}
                          </Box>
                          <Box sx={{ flex: '0 0 150px', minWidth: 150, p: 1, display: 'flex', alignItems: 'center', overflow: 'hidden' }}>
                            {isEditing ? (
                              <TextField
                                size="small"
                                value={editingMemberData?.contactNumber || ''}
                                onChange={(e) => handleChangeMemberData('contactNumber', e.target.value)}
                                fullWidth
                                sx={{ fontSize: '0.875rem' }}
                              />
                            ) : (
                              row.contactNumber
                            )}
                          </Box>
                          <Box sx={{ flex: '0 0 120px', minWidth: 120, p: 1, display: 'flex', alignItems: 'center', justifyContent: 'flex-end', overflow: 'hidden' }}>
                            {isEditing ? (
                              <TextField
                                size="small"
                                type="number"
                                value={editingMemberData?.commitment || 0}
                                onChange={(e) => handleChangeMemberData('commitment', Number(e.target.value))}
                                fullWidth
                                sx={{ fontSize: '0.875rem' }}
                              />
                            ) : (
                              row.commitment
                            )}
                          </Box>
                          <Box sx={{ flex: '0 0 100px', minWidth: 100, p: 1, display: 'flex', alignItems: 'center', justifyContent: 'flex-end', overflow: 'hidden' }}>
                            {isEditing ? (
                              <TextField
                                size="small"
                                type="number"
                                value={editingMemberData?.paid || 0}
                                onChange={(e) => handleChangeMemberData('paid', Number(e.target.value))}
                                fullWidth
                                sx={{ fontSize: '0.875rem' }}
                              />
                            ) : (
                              row.paid
                            )}
                          </Box>
                          <Box sx={{ flex: '0 0 100px', minWidth: 100, p: 1, display: 'flex', alignItems: 'center', justifyContent: 'flex-end', overflow: 'hidden' }}>
                            {isEditing ? (
                              <TextField
                                size="small"
                                type="number"
                                value={editingMemberData?.due || 0}
                                onChange={(e) => handleChangeMemberData('due', Number(e.target.value))}
                                fullWidth
                                sx={{ fontSize: '0.875rem' }}
                              />
                            ) : (
                              row.due
                            )}
                          </Box>
                          <Box sx={{ flex: '0 0 250px', minWidth: 250, p: 1, display: 'flex', alignItems: 'center', overflow: 'hidden' }}>
                            {isEditing ? (
                              <TextField
                                size="small"
                                value={editingMemberData?.remarks || ''}
                                onChange={(e) => handleChangeMemberData('remarks', e.target.value)}
                                fullWidth
                                sx={{ fontSize: '0.875rem' }}
                              />
                            ) : (
                              row.remarks
                            )}
                          </Box>
                          <Box sx={{ flex: '0 0 100px', minWidth: 100, p: 1, display: 'flex', alignItems: 'center', gap: 0.5 }}>
                            {isMaintainer && !isEditing && (
                              <>
                                <IconButton
                                  size="small"
                                  onClick={() => handleEditMember(row)}
                                  title="Edit"
                                >
                                  <EditIcon fontSize="small" />
                                </IconButton>
                                <IconButton
                                  size="small"
                                  onClick={() => {
                                    if (window.confirm('Are you sure you want to delete this member record?')) {
                                      handleDeleteMember(row.documentId);
                                    }
                                  }}
                                  disabled={savingMember}
                                  title="Delete"
                                >
                                  <DeleteIcon fontSize="small" />
                                </IconButton>
                              </>
                            )}
                            {isMaintainer && isEditing && (
                              <>
                                <IconButton
                                  size="small"
                                  onClick={handleSaveMember}
                                  disabled={savingMember}
                                  title="Save"
                                >
                                  <SaveIcon fontSize="small" />
                                </IconButton>
                                <IconButton
                                  size="small"
                                  onClick={handleCancelMember}
                                  title="Cancel"
                                >
                                  <CancelIcon fontSize="small" />
                                </IconButton>
                              </>
                            )}
                          </Box>
                        </Box>
                      );
                    })
                  ) : (
                    <Box sx={{ p: 3, textAlign: 'center', color: '#666', minWidth: '100%' }}>
                      No member records found
                    </Box>
                  )}
                </Box>
              </Box>
            )}
          </TabPanel>
        </Paper>
      </Container>

      {/* Attachment Viewer for Other Sources */}
      {selectedOtherSourceAttachment && (
        <AttachmentViewer
          open={otherSourceViewerOpen}
          onClose={() => {
            setOtherSourceViewerOpen(false);
            setSelectedOtherSourceAttachment(null);
          }}
          attachments={[{
            url: selectedOtherSourceAttachment.url,
            name: selectedOtherSourceAttachment.name,
            mime: selectedOtherSourceAttachment.mime,
            ext: selectedOtherSourceAttachment.ext,
          }]}
          initialIndex={0}
          apiBaseUrl={API_BASE_URL}
        />
      )}
    </Box>
  );
};

export default BudgetCommitment;
